import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  ProgressBar,
  Row,
  Spinner,
} from "react-bootstrap";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EventIcon from "@mui/icons-material/Event";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TuneIcon from "@mui/icons-material/Tune";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import { Box, Typography, alpha, Stack, Paper } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { startOfMonth, addMonths } from "date-fns";
import CalendarMonthGrid from "../Components/CalendarMonthGrid";
import PageLayout from "../Components/PageLayout";
import {
  schedulingApi,
  filterCoursesByScheduleEventType,
  courseMatchesScheduleEventType,
} from "../Components/schedulingApi";
import { toast } from "react-toastify";
import styles from "./CreateEnquiryWizard.module.scss";
import {
  SCHEDULE_TYPE_UNIT_SELECTION,
  defaultUnitStructureRows,
  validateUnitStructure,
  validateUnitInstancesAgainstStructureRows,
  buildScheduledSlotsForUnitSelection,
  UNIT_SELECTION_DAY_LABELS,
  structureRowTargetMinutes,
  addMinutesToHmTime,
  legacyWeek1SundayToMondayYmd,
} from "./unitSelectionSchedule";

/** Allowed schedule event types (must match backend EVENT_TYPES). */
const SCHEDULE_EVENT_TYPES = ["class", "exam", "seminar", "unit"];

/** Free-pick calendar: one distinct color per unit part index (cycles if many parts). */
const FREE_PICK_CALENDAR_COLORS = [
  "#5e72e4",
  "#2dce89",
  "#fb6340",
  "#11cdef",
  "#f5365c",
  "#8965e0",
  "#fd7e14",
  "#20c997",
];
const FREE_PICK_NON_UNIT_COLOR = "#64748b";

/** Max weekly occurrences for one free-pick row (safety cap). */
const FREE_PICK_MAX_REPEAT_WEEKS = 156;

/**
 * How many weekly occurrences this row generates (1 = no repeat / single week).
 * Uses repeat_weeks when set; otherwise falls back to legacy repeat_until_date.
 */
function freePickRepeatWeekCount(slot) {
  if (!slot || !slot.repeat_weekly) return 1;
  const rw = parseInt(String(slot.repeat_weeks ?? ""), 10);
  if (Number.isInteger(rw) && rw >= 1) return Math.min(rw, FREE_PICK_MAX_REPEAT_WEEKS);
  const date = String(slot.date || "").trim();
  const until = String(slot.repeat_until_date || "").trim();
  if (date && until) {
    const d0 = new Date(`${date}T12:00:00`);
    const d1 = new Date(`${until}T23:59:59`);
    if (!Number.isNaN(d0.getTime()) && !Number.isNaN(d1.getTime()) && d1 >= d0) {
      let cur = new Date(d0);
      let n = 0;
      while (cur <= d1) {
        n += 1;
        cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 7);
      }
      return Math.min(Math.max(1, n), FREE_PICK_MAX_REPEAT_WEEKS);
    }
  }
  return 1;
}

function freePickCalendarColorForSlot(isUnitCourse, unitComponentIndexStr) {
  if (!isUnitCourse) return FREE_PICK_NON_UNIT_COLOR;
  const idx = parseInt(unitComponentIndexStr, 10);
  if (!Number.isInteger(idx) || idx < 0) return FREE_PICK_CALENDAR_COLORS[0];
  return FREE_PICK_CALENDAR_COLORS[idx % FREE_PICK_CALENDAR_COLORS.length];
}

function unitComponentShortName(comp, idx) {
  if (comp) {
    const n = comp.session_type || comp.sessionType;
    if (n) return String(n);
  }
  return Number.isInteger(idx) && idx >= 0 ? `Part ${idx + 1}` : "Unit session";
}

/** Default lecturer from weekly plan row for a session type. */
function lecturerIdFromStructureRow(structureRows, componentIndex) {
  const row = (structureRows || []).find(
    (r) => parseInt(String(r.component_index), 10) === parseInt(String(componentIndex), 10)
  );
  const lid = row?.lecturer_id;
  return lid != null && String(lid).trim() !== "" ? String(lid) : "";
}

function isUnitCourse(course) {
  if (!course) return false;
  const t = String(course.type || "").toLowerCase();
  const comps = course.session_components;
  return t === "unit" && Array.isArray(comps) && comps.length > 0;
}

function formatMinutesHuman(totalMinutes) {
  const n = Number(totalMinutes);
  if (!Number.isFinite(n) || n < 0) return "—";
  const safe = Math.round(n);
  const h = Math.floor(safe / 60);
  const min = safe % 60;
  if (h === 0) return `${min} min`;
  if (min === 0) return `${h} h`;
  return `${h} h ${min} min`;
}

/** Minutes from course JSON (for unit parts: total contact time to schedule across all rows/repeats in free pick). */
function unitComponentDurationMinutes(comp) {
  if (!comp) return null;
  const dm = comp.duration_minutes ?? comp.durationMinutes;
  const n = Number(dm);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

function unitComponentLabel(comp) {
  if (!comp) return "—";
  const name = comp.session_type || comp.sessionType || "Session";
  const mins = unitComponentDurationMinutes(comp);
  return mins != null ? `${name} (${formatMinutesHuman(mins)})` : name;
}

/** Label in free-pick UI: makes clear the course value is a total, not one meeting length. */
function unitComponentFreePickLabel(comp) {
  if (!comp) return "—";
  const name = comp.session_type || comp.sessionType || "Session";
  const mins = unitComponentDurationMinutes(comp);
  return mins != null ? `${name} (total ${formatMinutesHuman(mins)})` : name;
}

/** Merge unit session index into form state: duration, event_type, end_datetime. */
function applyUnitComponentToFormState(base, course, indexStr) {
  const comps = isUnitCourse(course) ? course.session_components : [];
  const idx = parseInt(indexStr, 10);
  const comp = Number.isInteger(idx) && idx >= 0 && idx < comps.length ? comps[idx] : null;
  const dm = unitComponentDurationMinutes(comp);
  const updates = { ...base, unit_component_index: indexStr };
  if (dm != null && comp) {
    updates.duration_minutes = dm;
    updates.event_type = "unit";
    const start = parseAsUtc(updates.start_datetime);
    if (start) {
      updates.end_datetime = new Date(start.getTime() + dm * 60000).toISOString().slice(0, 19);
    }
  }
  return updates;
}
const DAYS_OF_WEEK = [
  { value: 0, label: "Mon" },
  { value: 1, label: "Tue" },
  { value: 2, label: "Wed" },
  { value: 3, label: "Thu" },
  { value: 4, label: "Fri" },
  { value: 5, label: "Sat" },
  { value: 6, label: "Sun" },
];

// Treat stored ISO strings as UTC so start/end stay consistent across timezones
function parseAsUtc(isoString) {
  if (!isoString || typeof isoString !== "string") return null;
  const s = isoString.trim();
  const normalized = s.length <= 19 && !s.endsWith("Z") ? s + "Z" : s;
  return new Date(normalized);
}
function localDatetimeToIsoUtc(localValue) {
  if (!localValue || typeof localValue !== "string") return "";
  const d = new Date(localValue);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 19);
}
function isoUtcToDatetimeLocalValue(isoString) {
  if (!isoString || typeof isoString !== "string") return "";
  const d = parseAsUtc(isoString);
  if (!d || Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function generateRecurrenceOccurrences(options) {
  const { recurrence_start_date, recurrence_end_date, recurrence_frequency, recurrence_days_of_week, recurrence_start_time, duration_minutes } = options;
  const startDate = recurrence_start_date ? new Date(recurrence_start_date + "T00:00:00") : null;
  const endDate = recurrence_end_date ? new Date(recurrence_end_date + "T23:59:59") : null;
  const timePart = (recurrence_start_time || "09:00").trim();
  const duration = Math.max(1, Number(duration_minutes) || 60);
  if (!startDate || !endDate || Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return [];

  const freq = (recurrence_frequency || "").toLowerCase();
  const occurrences = [];

  if (freq === "daily") {
    const d = new Date(startDate);
    while (d <= endDate) {
      const [hh, mm] = timePart.split(":").map(Number);
      const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), isNaN(hh) ? 9 : hh, isNaN(mm) ? 0 : mm, 0, 0);
      if (start >= startDate && start <= endDate) occurrences.push(start);
      d.setDate(d.getDate() + 1);
    }
  } else if (freq === "weekly" && Array.isArray(recurrence_days_of_week) && recurrence_days_of_week.length > 0) {
    const daysSet = new Set(recurrence_days_of_week.map(Number));
    const d = new Date(startDate);
    while (d <= endDate) {
      const dayOfWeek = (d.getDay() + 6) % 7;
      if (daysSet.has(dayOfWeek)) {
        const [hh, mm] = timePart.split(":").map(Number);
        const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), isNaN(hh) ? 9 : hh, isNaN(mm) ? 0 : mm, 0, 0);
        if (start >= startDate && start <= endDate) occurrences.push(start);
      }
      d.setDate(d.getDate() + 1);
    }
  } else if (freq === "monthly") {
    const dayOfMonth = startDate.getDate();
    let d = new Date(startDate.getFullYear(), startDate.getMonth(), Math.min(dayOfMonth, new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate()));
    while (d <= endDate) {
      const [hh, mm] = timePart.split(":").map(Number);
      const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), isNaN(hh) ? 9 : hh, isNaN(mm) ? 0 : mm, 0, 0);
      if (start >= startDate && start <= endDate) occurrences.push(start);
      d.setMonth(d.getMonth() + 1);
      d.setDate(Math.min(dayOfMonth, new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()));
    }
  }

  return occurrences.map((start) => {
    const end = new Date(start.getTime() + duration * 60000);
    return {
      start_datetime: start.toISOString().slice(0, 19),
      end_datetime: end.toISOString().slice(0, 19),
    };
  });
}

function parseCourseDurationMinutes(durationValue) {
  if (durationValue == null) return null;
  const raw = String(durationValue).trim();
  if (!raw) return null;

  const asNumber = Number(raw);
  if (Number.isFinite(asNumber) && asNumber >= 0) {
    return Math.round(asNumber);
  }

  // Support values like "2h 30m", "2 hours", "30 min".
  let total = 0;
  const hourMatch = raw.match(/(\d+(?:\.\d+)?)\s*(h|hr|hrs|hour|hours)\b/i);
  if (hourMatch) total += Math.round(Number(hourMatch[1]) * 60);
  const minuteMatch = raw.match(/(\d+(?:\.\d+)?)\s*(m|min|mins|minute|minutes)\b/i);
  if (minuteMatch) total += Math.round(Number(minuteMatch[1]));
  return total > 0 ? total : null;
}

const freePickCalendarTheme = createTheme({
  palette: { primary: { main: "#5e72e4" }, background: { default: "#f8f9fe" } },
  typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", button: { textTransform: "none" } },
  shape: { borderRadius: 12 },
});

function formatDateYMD(d) {
  if (!d || typeof d.getTime !== "function" || Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Minutes between local wall times on one calendar day (YYYY-MM-DD). End must be after start same day. */
function minutesBetweenLocalDateTimes(dateStr, startTime, endTime) {
  if (!dateStr || !startTime || !endTime) return null;
  const st = String(startTime).trim();
  const en = String(endTime).trim();
  const a = new Date(`${dateStr}T${st.length === 5 ? `${st}:00` : st}`);
  const b = new Date(`${dateStr}T${en.length === 5 ? `${en}:00` : en}`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const diff = (b.getTime() - a.getTime()) / 60000;
  if (diff <= 0) return null;
  return Math.round(diff);
}

function expandFreePickSlotToScheduled(slot) {
  const { date, start_time, end_time, repeat_weekly } = slot;
  const mins = minutesBetweenLocalDateTimes(date, start_time, end_time);
  if (mins == null || mins <= 0) return [];

  const weekCount = freePickRepeatWeekCount(slot);
  const out = [];
  let cur = new Date(`${String(date).trim()}T12:00:00`);
  if (Number.isNaN(cur.getTime())) return [];

  for (let w = 0; w < weekCount; w++) {
    const ymd = formatDateYMD(cur);
    const t0 = String(start_time || "").trim();
    const t1 = String(end_time || "").trim();
    const startIso = t0 ? localDatetimeToIsoUtc(`${ymd}T${t0}`) : "";
    const endIso = t1 ? localDatetimeToIsoUtc(`${ymd}T${t1}`) : "";
    if (startIso && endIso) {
      const row = { start_datetime: startIso, end_datetime: endIso };
      const uciRaw = slot.unit_component_index;
      if (uciRaw != null && String(uciRaw).trim() !== "") {
        row.unit_component_index = uciRaw;
      }
      const link = String(slot.slot_meeting_link || "").trim();
      if (link) row.slot_meeting_link = link;
      const ridRaw = slot.classroom_id;
      const rid =
        ridRaw != null && String(ridRaw).trim() !== ""
          ? Number(ridRaw)
          : null;
      if (rid != null && !Number.isNaN(rid)) {
        row.classroom_id = rid;
      }
      // Online/hybrid with no room → fully online slot
      if (slot.hybrid_online_enabled && (rid == null || Number.isNaN(rid))) {
        row.is_online = true;
      }
      const lidRaw = slot.lecturer_id;
      if (lidRaw != null && String(lidRaw).trim() !== "") {
        const lid = Number(lidRaw);
        if (!Number.isNaN(lid)) row.lecturer_id = lid;
      }
      out.push(row);
    }
    if (!repeat_weekly) break;
    cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 7);
  }
  return out;
}

/** Calendar dates (YYYY-MM-DD) a free-pick row appears on (weekly = repeat_weeks occurrences). */
function freePickSlotOccurrenceDates(slot) {
  const date = String(slot?.date || "").trim();
  if (!date) return [];
  const repeatWeekly = !!slot.repeat_weekly;
  const weekCount = freePickRepeatWeekCount(slot);
  const out = [];
  let cur = new Date(`${date}T12:00:00`);
  if (Number.isNaN(cur.getTime())) return [date];
  for (let w = 0; w < weekCount; w++) {
    out.push(formatDateYMD(cur));
    if (!repeatWeekly) break;
    cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 7);
  }
  return out;
}

function totalExpandedFreePickMinutes(slots) {
  const list = Array.isArray(slots) ? slots : [];
  let sum = 0;
  for (const s of list) {
    for (const sl of expandFreePickSlotToScheduled(s)) {
      const st = parseAsUtc(sl.start_datetime);
      const en = parseAsUtc(sl.end_datetime);
      if (st && en && !Number.isNaN(st.getTime()) && !Number.isNaN(en.getTime())) {
        sum += (en.getTime() - st.getTime()) / 60000;
      }
    }
  }
  return Math.round(sum);
}

/** Total contact minutes required for this unit part (from course `duration_minutes`), incl. all repeats. */
function unitPartTotalTargetMinutes(comp) {
  return unitComponentDurationMinutes(comp);
}

function freePickExpandedMinutesForUnitPart(slots, partIndex, excludeSlotKey) {
  if (!Number.isInteger(partIndex) || partIndex < 0) return 0;
  const list = Array.isArray(slots) ? slots : [];
  let sum = 0;
  for (const s of list) {
    if (excludeSlotKey != null && s.key === excludeSlotKey) continue;
    const idx = parseInt(s.unit_component_index, 10);
    if (!Number.isInteger(idx) || idx !== partIndex) continue;
    for (const sl of expandFreePickSlotToScheduled(s)) {
      const st = parseAsUtc(sl.start_datetime);
      const en = parseAsUtc(sl.end_datetime);
      if (st && en && !Number.isNaN(st.getTime()) && !Number.isNaN(en.getTime())) {
        sum += (en.getTime() - st.getTime()) / 60000;
      }
    }
  }
  return Math.round(sum);
}

function freePickDraftExpandedMinutes(draft) {
  if (!draft) return 0;
  let sum = 0;
  for (const sl of expandFreePickSlotToScheduled(draft)) {
    const st = parseAsUtc(sl.start_datetime);
    const en = parseAsUtc(sl.end_datetime);
    if (st && en && !Number.isNaN(st.getTime()) && !Number.isNaN(en.getTime())) {
      sum += (en.getTime() - st.getTime()) / 60000;
    }
  }
  return Math.round(sum);
}

const getDefaultFormData = (initialValues = {}) => {
  const base = {
    title: "",
    event_type: "class",
    schedule_type: "one_time",
    recurrence_frequency: "",
    recurrence_start_date: "",
    recurrence_end_date: "",
    recurrence_days_of_week: [],
    recurrence_start_time: "09:00",
    course_id: "",
    lecturer_id: "",
    classroom_id: "",
    start_datetime: "",
    end_datetime: "",
    duration_minutes: 60,
    capacity: 0,
    enrollment_required: false,
    allow_waitlist: false,
    meeting_link: "",
    description: "",
    unit_component_index: "",
    unit_schedule_all: false,
    unit_session_starts: [],
    unit_session_lecturers: [],
    free_pick_slots: [],
    unit_total_weeks: 4,
    unit_week1_monday: "",
    unit_structure_rows: [],
    unit_selection_instances: [],
  };
  const merged = { ...base, ...initialValues };
  merged.course_id = merged.course_id ?? "";
  merged.lecturer_id = merged.lecturer_id ?? "";
  merged.classroom_id = merged.classroom_id ?? "";
  merged.unit_component_index =
    merged.unit_component_index !== undefined && merged.unit_component_index !== null
      ? String(merged.unit_component_index)
      : "";
  merged.unit_schedule_all = Boolean(merged.unit_schedule_all);
  merged.unit_session_starts = Array.isArray(merged.unit_session_starts) ? merged.unit_session_starts : [];
  merged.unit_session_lecturers = Array.isArray(merged.unit_session_lecturers)
    ? merged.unit_session_lecturers
    : [];
  merged.free_pick_slots = Array.isArray(merged.free_pick_slots) ? merged.free_pick_slots : [];
  merged.unit_total_weeks = merged.unit_total_weeks != null ? Number(merged.unit_total_weeks) : 4;
  {
    const mon =
      merged.unit_week1_monday != null && String(merged.unit_week1_monday).trim() !== ""
        ? String(merged.unit_week1_monday).trim()
        : "";
    const legSun =
      merged.unit_week1_sunday != null && String(merged.unit_week1_sunday).trim() !== ""
        ? String(merged.unit_week1_sunday).trim()
        : "";
    merged.unit_week1_monday = mon || (legSun ? legacyWeek1SundayToMondayYmd(legSun) : "");
  }
  delete merged.unit_week1_sunday;
  merged.unit_structure_rows = Array.isArray(merged.unit_structure_rows) ? merged.unit_structure_rows : [];
  merged.unit_selection_instances = Array.isArray(merged.unit_selection_instances) ? merged.unit_selection_instances : [];
  if (merged.schedule_type === "recurring") {
    merged.schedule_type = "one_time";
    merged.recurrence_frequency = "";
    merged.recurrence_start_date = "";
    merged.recurrence_end_date = "";
    merged.recurrence_days_of_week = [];
    merged.recurrence_start_time = "09:00";
  }
  if (merged.start_datetime && merged.duration_minutes && !merged.end_datetime) {
    const start = parseAsUtc(merged.start_datetime);
    if (start) {
      merged.end_datetime = new Date(start.getTime() + (merged.duration_minutes || 60) * 60000).toISOString().slice(0, 19);
    }
  }
  return merged;
};

const STEP_META_BASE = [
  {
    id: 1,
    short: "Basics",
    title: "Title & Type",
    icon: <EventIcon fontSize="small" />,
  },
  {
    id: 2,
    short: "Schedule",
    title: "Time & Room",
    icon: <ScheduleIcon fontSize="small" />,
  },
  {
    id: 3,
    short: "Options",
    title: "Capacity & Links",
    icon: <TuneIcon fontSize="small" />,
  },
  {
    id: 4,
    short: "Review",
    title: "Review & Submit",
    icon: <PreviewOutlinedIcon fontSize="small" />,
  },
];

const STEP_META_UNIT_SELECTION = [
  {
    id: 1,
    short: "Basics",
    title: "Title & Type",
    icon: <EventIcon fontSize="small" />,
  },
  {
    id: 2,
    short: "Weekly plan",
    title: "Plan the teaching weeks",
    icon: <ScheduleIcon fontSize="small" />,
  },
  {
    id: 3,
    short: "Instances",
    title: "Place sessions",
    icon: <ScheduleIcon fontSize="small" />,
  },
  {
    id: 4,
    short: "Review",
    title: "Review & Submit",
    icon: <PreviewOutlinedIcon fontSize="small" />,
  },
];

const SCHEDULE_CREATOR_DRAFT_KEY = "ems.scheduleCreatorDraft.v1";

function loadScheduleCreatorDraft() {
  try {
    const raw = window.localStorage.getItem(SCHEDULE_CREATOR_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function saveScheduleCreatorDraft(draft) {
  try {
    window.localStorage.setItem(SCHEDULE_CREATOR_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Ignore quota/private mode errors.
  }
}

function clearScheduleCreatorDraft() {
  try {
    window.localStorage.removeItem(SCHEDULE_CREATOR_DRAFT_KEY);
  } catch {
    // noop
  }
}

/** Split stored minutes into whole hours and remainder (for step 2 inputs). */
function splitTotalMinutesToHm(totalMins) {
  const t = Math.max(0, Math.round(Number(totalMins) || 0));
  return { hours: Math.floor(t / 60), minutes: t % 60 };
}

function formatDurationHmLabel(totalMins) {
  const { hours, minutes } = splitTotalMinutesToHm(totalMins);
  if (hours <= 0) return `${minutes} min`;
  if (minutes === 0) return hours === 1 ? "1 hr" : `${hours} hr`;
  return `${hours} hr ${minutes} min`;
}

/** Integer from a number input; empty string → null so the field can be cleared while editing. */
function intFromOptionalInput(raw) {
  if (raw === "" || raw == null) return null;
  const n = parseInt(String(raw).trim(), 10);
  return Number.isFinite(n) ? n : null;
}

/** Same part/day/time/room|online/capacity/lecturer — excludes week_number (groups multi-week clones). */
function logicalUnitSlotSignature(inst) {
  if (!inst) return "";
  return [
    parseInt(String(inst.component_index), 10),
    parseInt(String(inst.day_of_week), 10),
    String(inst.start_time || ""),
    String(inst.end_time || ""),
    Boolean(inst.is_online) ? 1 : 0,
    Boolean(inst.hybrid_online_enabled) ? 1 : 0,
    String(inst.classroom_id || ""),
    String(inst.slot_meeting_link || "").trim(),
    String(inst.slot_capacity ?? ""),
    String(inst.lecturer_id ?? ""),
  ].join("|");
}

function keysForLogicalUnitSlotGroup(allInsts, inst) {
  const sig = logicalUnitSlotSignature(inst);
  return (allInsts || [])
    .filter((x) => logicalUnitSlotSignature(x) === sig)
    .map((x) => x.key);
}

function clusterUnitSlotsForDayColumn(allInsts, compIdx, day) {
  const filtered = (allInsts || []).filter(
    (i) => parseInt(String(i.component_index), 10) === compIdx && parseInt(String(i.day_of_week), 10) === day
  );
  const m = new Map();
  for (const inst of filtered) {
    const sig = logicalUnitSlotSignature(inst);
    if (!m.has(sig)) m.set(sig, { rep: inst, weeks: [] });
    m.get(sig).weeks.push(parseInt(String(inst.week_number), 10));
  }
  return Array.from(m.values())
    .map(({ rep, weeks }) => {
      const sorted = [...new Set(weeks.filter((n) => Number.isFinite(n)))].sort((a, b) => a - b);
      const lo = sorted[0] ?? (parseInt(String(rep.week_number), 10) || 1);
      const hi = sorted[sorted.length - 1] ?? lo;
      return {
        rep,
        weekLabel: lo === hi ? `Week ${lo}` : `Weeks ${lo}–${hi}`,
      };
    })
    .sort((a, b) => String(a.rep.start_time).localeCompare(String(b.rep.start_time)));
}

function firstUnitInstErrorInGroup(allInsts, errors, step3Attempted, repInst) {
  if (!step3Attempted || !repInst) return null;
  const sig = logicalUnitSlotSignature(repInst);
  for (let i = 0; i < (allInsts || []).length; i += 1) {
    if (logicalUnitSlotSignature(allInsts[i]) === sig && errors[`unit_inst_${i}`]) {
      return errors[`unit_inst_${i}`];
    }
  }
  return null;
}

/** Event-level capacity for unit selection: max of per-slot cap or room capacity (0 = no limit). */
function computeCapacityFromUnitInstances(instances, classrooms) {
  const rooms = classrooms || [];
  let maxCap = 0;
  for (const inst of instances || []) {
    const capRaw = inst.slot_capacity;
    if (capRaw != null && String(capRaw).trim() !== "") {
      const n = Number(capRaw);
      if (!Number.isNaN(n) && n >= 1) {
        maxCap = Math.max(maxCap, Math.floor(n));
        continue;
      }
    }
    if (inst.is_online) continue;
    const rid = inst.classroom_id;
    if (rid == null || String(rid).trim() === "") continue;
    const room = rooms.find((r) => String(r.id) === String(rid));
    const c = room?.capacity;
    if (c != null && Number.isFinite(Number(c)) && Number(c) >= 0) {
      maxCap = Math.max(maxCap, Math.floor(Number(c)));
    }
  }
  return maxCap;
}

const STEP_FIELDS = {
  1: [
    "title",
    "event_type",
    "schedule_type",
    "recurrence_frequency",
    "recurrence_start_date",
    "recurrence_end_date",
    "recurrence_days_of_week",
    "course_id",
    "unit_component_index",
    "unit_schedule_all",
    "lecturer_id",
  ],
  2: [
    "classroom_id",
    "start_datetime",
    "recurrence_start_time",
    "duration_minutes",
    "capacity",
    "free_pick_slots",
    "free_pick_classroom",
    "free_pick_unit_budget",
  ],
  3: ["enrollment_required", "allow_waitlist", "meeting_link", "description"],
};

export default function CreateEventWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialFromState = location.state?.initialValues || {};

  const [options, setOptions] = useState({ courses: [], lecturers: [], classrooms: [] });
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [formData, setFormData] = useState(() => getDefaultFormData(initialFromState));
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [stepAttempted, setStepAttempted] = useState({ 1: false, 2: false, 3: false, 4: false, 5: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdId, setCreatedId] = useState("");
  const [recurringCreatedCount, setRecurringCreatedCount] = useState(0);
  const [recurringSkippedCount, setRecurringSkippedCount] = useState(0);
  const [unitAllSuccessCount, setUnitAllSuccessCount] = useState(0);
  const [freePickSuccessCount, setFreePickSuccessCount] = useState(0);
  const [freePickCalendarMonth, setFreePickCalendarMonth] = useState(() => startOfMonth(new Date()));
  const [freePickModalShow, setFreePickModalShow] = useState(false);
  const [freePickDraft, setFreePickDraft] = useState(null);
  const [unitInstModalShow, setUnitInstModalShow] = useState(false);
  const [unitInstDraft, setUnitInstDraft] = useState(null);
  /** Keys of all instances edited together (same logical slot across weeks); null when adding new. */
  const [unitInstGroupKeys, setUnitInstGroupKeys] = useState(null);
  const [unitStructureErrorAlertDismissed, setUnitStructureErrorAlertDismissed] = useState(false);
  const [unitInstancesBudgetAlertDismissed, setUnitInstancesBudgetAlertDismissed] = useState(false);
  const [draftHydrated, setDraftHydrated] = useState(false);

  const selectedCourseForWizard = (options.courses || []).find((c) => String(c.id) === String(formData.course_id));
  const selectedCourseIsUnit = isUnitCourse(selectedCourseForWizard);
  const unitSelectionMode = selectedCourseIsUnit && formData.schedule_type === SCHEDULE_TYPE_UNIT_SELECTION;
  const totalSteps = 4;
  const stepMeta = unitSelectionMode ? STEP_META_UNIT_SELECTION : STEP_META_BASE;
  const progressPercent = (currentStep / totalSteps) * 100;
  const currentStepMeta = stepMeta[Math.min(Math.max(currentStep, 1), totalSteps) - 1];
  const nextStepMeta = currentStep < totalSteps ? stepMeta[currentStep] : null;

  const loadOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const data = await schedulingApi.getOptions();
      if (data?.error) {
        toast.error(data.error);
        setOptions({ courses: [], lecturers: [], classrooms: [] });
        return;
      }
      setOptions({
        courses: data.courses || [],
        lecturers: data.lecturers || [],
        classrooms: data.classrooms || [],
      });
    } catch (e) {
      toast.error(e?.message || "Failed to load options");
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    const hasPrefilledState = Object.keys(initialFromState || {}).length > 0;
    if (hasPrefilledState) {
      setDraftHydrated(true);
      return;
    }

    const draft = loadScheduleCreatorDraft();
    if (!draft) {
      setDraftHydrated(true);
      return;
    }

    if (draft.formData && typeof draft.formData === "object") {
      setFormData(getDefaultFormData(draft.formData));
    }
    if (Number.isInteger(draft.currentStep) && draft.currentStep >= 1 && draft.currentStep <= totalSteps) {
      setCurrentStep(draft.currentStep);
    }
    setFreePickDraft(draft.freePickDraft ?? null);
    setUnitInstDraft(draft.unitInstDraft ?? null);
    setUnitInstGroupKeys(Array.isArray(draft.unitInstGroupKeys) ? draft.unitInstGroupKeys : null);
    toast.info("Restored unsaved Schedule Creator draft.");
    setDraftHydrated(true);
  }, []);

  useEffect(() => {
    if (!draftHydrated) return;
    saveScheduleCreatorDraft({
      savedAt: new Date().toISOString(),
      formData,
      currentStep,
      freePickDraft,
      unitInstDraft,
      unitInstGroupKeys,
    });
  }, [draftHydrated, formData, currentStep, freePickDraft, unitInstDraft, unitInstGroupKeys]);

  /** Unit courses only use the timetable wizard (not one-time / recurring on step 1). */
  useEffect(() => {
    if (!selectedCourseIsUnit || !selectedCourseForWizard) return;
    setFormData((prev) => {
      if (prev.schedule_type === SCHEDULE_TYPE_UNIT_SELECTION) return prev;
      return {
        ...prev,
        schedule_type: SCHEDULE_TYPE_UNIT_SELECTION,
        unit_schedule_all: false,
        free_pick_slots: [],
        recurrence_frequency: "",
        recurrence_start_date: "",
        recurrence_end_date: "",
        recurrence_days_of_week: [],
        recurrence_start_time: "09:00",
        lecturer_id: "",
        unit_structure_rows: defaultUnitStructureRows(selectedCourseForWizard),
        unit_selection_instances: [],
      };
    });
  }, [selectedCourseIsUnit, selectedCourseForWizard?.id]);

  useEffect(() => {
    if (currentStep > totalSteps) setCurrentStep(totalSteps);
  }, [currentStep, totalSteps]);

  const updateUnitSessionStart = useCallback(
    (idx, localDatetimeValue) => {
      setFormData((prev) => {
        const course = (options.courses || []).find((c) => String(c.id) === String(prev.course_id));
        const n = isUnitCourse(course) ? course.session_components.length : 0;
        const arr = Array.from({ length: n }, (_, i) => (Array.isArray(prev.unit_session_starts) ? prev.unit_session_starts[i] : "") || "");
        arr[idx] = localDatetimeValue ? localDatetimeToIsoUtc(localDatetimeValue) : "";
        return { ...prev, unit_session_starts: arr };
      });
      setErrors((e) => ({ ...e, [`unit_slot_${idx}`]: null }));
    },
    [options.courses]
  );

  const updateUnitSessionLecturer = useCallback((idx, lecturerId) => {
    setFormData((prev) => {
      const course = (options.courses || []).find((c) => String(c.id) === String(prev.course_id));
      const n = isUnitCourse(course) ? course.session_components.length : 0;
      const arr = Array.from(
        { length: n },
        (_, i) => (Array.isArray(prev.unit_session_lecturers) ? prev.unit_session_lecturers[i] : "") || ""
      );
      arr[idx] = lecturerId != null ? String(lecturerId) : "";
      return { ...prev, unit_session_lecturers: arr };
    });
  }, [options.courses]);

  const chainUnitSessionStarts = useCallback(() => {
    setFormData((prev) => {
      const course = (options.courses || []).find((c) => String(c.id) === String(prev.course_id));
      if (!isUnitCourse(course) || !prev.unit_schedule_all) return prev;
      const comps = course.session_components || [];
      const arr = Array.from({ length: comps.length }, (_, i) => (Array.isArray(prev.unit_session_starts) ? prev.unit_session_starts[i] : "") || "");
      const firstIso = (arr[0] || "").trim();
      const t0 = parseAsUtc(firstIso);
      if (!t0 || Number.isNaN(t0.getTime())) return prev;
      let t = t0;
      for (let i = 0; i < comps.length; i++) {
        arr[i] = t.toISOString().slice(0, 19);
        const dm = unitComponentDurationMinutes(comps[i]) || 60;
        t = new Date(t.getTime() + dm * 60000);
      }
      return { ...prev, unit_session_starts: arr };
    });
  }, [options.courses]);

  const setFieldValue = (field, value) => {
    if (field === "classroom_id") {
      const selectedClassroom = (options.classrooms || []).find(
        (room) => String(room.id) === String(value)
      );
      setFormData((prev) => ({
        ...prev,
        capacity: selectedClassroom?.capacity ?? 0,
      }));
    }
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "event_type") {
        const course = (options.courses || []).find((c) => String(c.id) === String(next.course_id));
        if (course && !courseMatchesScheduleEventType(course, value)) {
          next.course_id = "";
          next.unit_component_index = "";
          next.unit_schedule_all = false;
          next.unit_session_starts = [];
          next.free_pick_slots = [];
          next.duration_minutes = 60;
          next.start_datetime = "";
          next.end_datetime = "";
        }
      }
      if (field === "course_id") {
        const course = (options.courses || []).find((c) => String(c.id) === String(value));
        next.unit_schedule_all = false;
        next.free_pick_slots = [];
        next.unit_structure_rows = [];
        next.unit_selection_instances = [];
        next.unit_week1_monday = "";
        if (isUnitCourse(course)) {
          const n = course.session_components.length;
          next.unit_session_starts = Array.from({ length: n }, () => "");
          next.unit_session_lecturers = Array.from({ length: n }, () => "");
          next.schedule_type = SCHEDULE_TYPE_UNIT_SELECTION;
          next.unit_structure_rows = defaultUnitStructureRows(course);
          next.lecturer_id = "";
          Object.assign(next, applyUnitComponentToFormState(next, course, "0"));
        } else {
          next.unit_component_index = "";
          next.unit_session_starts = [];
          next.unit_session_lecturers = [];
          const prevCourse = (options.courses || []).find((c) => String(c.id) === String(prev.course_id));
          if (isUnitCourse(prevCourse)) {
            next.lecturer_id = "";
          }
          if (next.schedule_type === SCHEDULE_TYPE_UNIT_SELECTION) {
            next.schedule_type = "one_time";
          }
          if (String(next.event_type || "").toLowerCase() === "unit") {
            next.event_type = "class";
          }
        }
      }
      if (field === "unit_schedule_all") {
        next.unit_schedule_all = Boolean(value);
        const course = (options.courses || []).find((c) => String(c.id) === String(next.course_id));
        if (next.unit_schedule_all && isUnitCourse(course)) {
          const n = course.session_components.length;
          const prevStarts = Array.isArray(prev.unit_session_starts) ? prev.unit_session_starts : [];
          const prevLecturers = Array.isArray(prev.unit_session_lecturers) ? prev.unit_session_lecturers : [];
          next.unit_session_starts = Array.from({ length: n }, (_, i) => prevStarts[i] || "");
          next.unit_session_lecturers = Array.from({ length: n }, (_, i) => prevLecturers[i] || "");
          next.schedule_type = "one_time";
          next.free_pick_slots = [];
          next.recurrence_frequency = "";
          next.recurrence_start_date = "";
          next.recurrence_end_date = "";
          next.recurrence_days_of_week = [];
          next.recurrence_start_time = "09:00";
        }
      }
      if (field === "schedule_type" && value === SCHEDULE_TYPE_UNIT_SELECTION) {
        next.unit_schedule_all = false;
        next.free_pick_slots = [];
        next.recurrence_frequency = "";
        next.recurrence_start_date = "";
        next.recurrence_end_date = "";
        next.recurrence_days_of_week = [];
        next.recurrence_start_time = "09:00";
        const course = (options.courses || []).find((c) => String(c.id) === String(next.course_id));
        if (isUnitCourse(course)) {
          next.unit_structure_rows = defaultUnitStructureRows(course);
          next.unit_selection_instances = [];
        }
      }
      if (field === "schedule_type" && value === "free_pick") {
        next.unit_schedule_all = false;
        next.recurrence_frequency = "";
        next.recurrence_start_date = "";
        next.recurrence_end_date = "";
        next.recurrence_days_of_week = [];
        next.recurrence_start_time = "09:00";
      }
      if (field === "schedule_type" && value === "one_time") {
        if (prev.schedule_type === "free_pick") next.free_pick_slots = [];
        next.recurrence_frequency = "";
        next.recurrence_start_date = "";
        next.recurrence_end_date = "";
        next.recurrence_days_of_week = [];
        next.recurrence_start_time = "09:00";
      }
      if (field === "unit_component_index") {
        const course = (options.courses || []).find((c) => String(c.id) === String(next.course_id));
        Object.assign(next, applyUnitComponentToFormState(next, course, value));
      }
      if (field === "start_datetime") {
        if (!value) {
          next.start_datetime = "";
          next.end_datetime = "";
        } else {
          const stored = localDatetimeToIsoUtc(value);
          if (stored) {
            next.start_datetime = stored;
            const start = parseAsUtc(stored);
            const dm = next.duration_minutes;
            const mins =
              dm == null || dm === "" || !Number.isFinite(Number(dm)) || Number(dm) < 1
                ? 60
                : Math.max(1, Number(dm));
            next.end_datetime = new Date(start.getTime() + mins * 60000).toISOString().slice(0, 19);
          }
        }
      }
      if (field === "duration_minutes" && next.start_datetime) {
        const start = parseAsUtc(next.start_datetime);
        if (start) {
          const dmRaw = value;
          const mins =
            dmRaw == null || dmRaw === "" || !Number.isFinite(Number(dmRaw)) || Number(dmRaw) < 1
              ? 60
              : Math.max(1, Number(dmRaw));
          next.end_datetime = new Date(start.getTime() + mins * 60000).toISOString().slice(0, 19);
        }
      }
      return next;
    });
    STEP_FIELDS[1]?.includes(field) && setErrors((e) => ({ ...e, [field]: null }));
    STEP_FIELDS[2]?.includes(field) && setErrors((e) => ({ ...e, [field]: null }));
    STEP_FIELDS[3]?.includes(field) && setErrors((e) => ({ ...e, [field]: null }));
  };

  const validateStep1 = (data = formData) => {
    const nextErrors = {};
    if (!(data.title || "").trim()) nextErrors.title = "Title is required.";
    if (!String(data.course_id ?? "").trim()) nextErrors.course_id = "Course is required.";
    const selectedCourse = (options.courses || []).find((c) => String(c.id) === String(data.course_id));
    if (isUnitCourse(selectedCourse)) {
      if (
        !data.unit_schedule_all &&
        data.schedule_type !== "free_pick" &&
        data.schedule_type !== SCHEDULE_TYPE_UNIT_SELECTION
      ) {
        const idx = parseInt(data.unit_component_index, 10);
        const comps = selectedCourse.session_components || [];
        if (!Number.isInteger(idx) || idx < 0 || idx >= comps.length) {
          nextErrors.unit_component_index = "Select which part of the unit this session is for.";
        }
      }
    } else if (!SCHEDULE_EVENT_TYPES.includes(String(data.event_type || "").toLowerCase())) {
      nextErrors.event_type = "Choose class, exam, seminar, or unit.";
    }
    if (data.schedule_type === "recurring") {
      if (!(data.recurrence_frequency || "").trim()) nextErrors.recurrence_frequency = "Please select how often the event repeats.";
      if (!(data.recurrence_start_date || "").trim()) nextErrors.recurrence_start_date = "Recurrence start date is required.";
      if (!(data.recurrence_end_date || "").trim()) nextErrors.recurrence_end_date = "Recurrence end date is required.";
      if (data.recurrence_start_date && data.recurrence_end_date && data.recurrence_end_date < data.recurrence_start_date) {
        nextErrors.recurrence_end_date = "End date must be on or after start date.";
      }
      if ((data.recurrence_frequency || "").toLowerCase() === "weekly") {
        const days = Array.isArray(data.recurrence_days_of_week) ? data.recurrence_days_of_week : [];
        if (days.length === 0) nextErrors.recurrence_days_of_week = "Select at least one day of the week.";
      }
    }
    return nextErrors;
  };

  const validateUnitInstancesStep = (data = formData) => {
    const nextErrors = {};
    const selectedCourse = (options.courses || []).find((c) => String(c.id) === String(data.course_id));
    if (!isUnitCourse(selectedCourse) || data.schedule_type !== SCHEDULE_TYPE_UNIT_SELECTION) return nextErrors;
    if (!(data.unit_week1_monday || "").trim()) {
      nextErrors.unit_week1_monday = "Pick the Monday that starts week 1 of the unit.";
    }
    const insts = Array.isArray(data.unit_selection_instances) ? data.unit_selection_instances : [];
    if (insts.length === 0) {
      nextErrors.unit_instances = "Add at least one session from the timetable grid (click a day cell).";
    }
    insts.forEach((inst, i) => {
      const lab = `Row ${i + 1}: `;
      if (!(inst.start_time || "").trim() || !(inst.end_time || "").trim()) {
        nextErrors[`unit_inst_${i}`] = lab + "Start and end time are required.";
      }
      if (inst.is_online) {
        if (!(inst.slot_meeting_link || "").trim()) {
          nextErrors[`unit_inst_${i}`] =
            (nextErrors[`unit_inst_${i}`] ? `${nextErrors[`unit_inst_${i}`]} ` : "") + lab + "Meeting link is required for online instances.";
        }
      } else if (!String(inst.classroom_id || "").trim()) {
        nextErrors[`unit_inst_${i}`] = (nextErrors[`unit_inst_${i}`] ? `${nextErrors[`unit_inst_${i}`]} ` : "") + lab + "Classroom is required.";
      } else if (inst.hybrid_online_enabled && !(inst.slot_meeting_link || "").trim()) {
        nextErrors[`unit_inst_${i}`] =
          (nextErrors[`unit_inst_${i}`] ? `${nextErrors[`unit_inst_${i}`]} ` : "") + lab + "Meeting link is required for hybrid instances.";
      }
      const capRaw = inst.slot_capacity;
      if (capRaw != null && String(capRaw).trim() !== "") {
        const n = Number(capRaw);
        if (Number.isNaN(n) || n < 1) {
          nextErrors[`unit_inst_${i}`] =
            (nextErrors[`unit_inst_${i}`] ? `${nextErrors[`unit_inst_${i}`]} ` : "") + lab + "Capacity must be a positive integer.";
        }
      }
    });
    const budgetErrs = validateUnitInstancesAgainstStructureRows(
      selectedCourse,
      data.unit_structure_rows || [],
      insts,
      data.unit_week1_monday
    );
    if (budgetErrs.length) {
      nextErrors.unit_instances_budget = budgetErrs;
    }
    return nextErrors;
  };

  const validateStep2 = (data = formData) => {
    const nextErrors = {};
    const dur = Number(data.duration_minutes);
    const selectedCourse = (options.courses || []).find((c) => String(c.id) === String(data.course_id));
    const unitAll = isUnitCourse(selectedCourse) && data.unit_schedule_all;

    if (isUnitCourse(selectedCourse) && data.schedule_type === SCHEDULE_TYPE_UNIT_SELECTION) {
      const errs = validateUnitStructure(selectedCourse, data.unit_total_weeks, data.unit_structure_rows || []);
      if (errs.length) {
        nextErrors.unit_structure = errs;
      }
      return nextErrors;
    }

    if (data.schedule_type === "free_pick") {
      const slots = Array.isArray(data.free_pick_slots) ? data.free_pick_slots : [];
      const roomSet = new Set();
      if (slots.length === 0) {
        nextErrors.free_pick_slots = "Add at least one session by clicking a date on the calendar.";
      }
      slots.forEach((s, i) => {
        const lab = `Row ${i + 1}: `;
        if (!(s.date || "").trim()) {
          nextErrors[`free_pick_slot_${i}`] = lab + "Date is required.";
          return;
        }
        if (!(s.start_time || "").trim()) {
          nextErrors[`free_pick_slot_${i}`] = lab + "Start time is required.";
          return;
        }
        if (!(s.end_time || "").trim()) {
          nextErrors[`free_pick_slot_${i}`] = lab + "End time is required.";
          return;
        }
        const mins = minutesBetweenLocalDateTimes(s.date, s.start_time, s.end_time);
        if (mins == null) {
          nextErrors[`free_pick_slot_${i}`] = lab + "End time must be after start time on the same day.";
        }
        const rid = String(s.classroom_id || "").trim();
        if (!rid && !s.hybrid_online_enabled) {
          nextErrors[`free_pick_slot_${i}`] = nextErrors[`free_pick_slot_${i}`]
            ? `${nextErrors[`free_pick_slot_${i}`]} Classroom is required.`
            : lab + "Classroom is required.";
        } else if (rid) {
          roomSet.add(rid);
        }
        if (s.hybrid_online_enabled && !String(s.slot_meeting_link || "").trim()) {
          nextErrors[`free_pick_slot_${i}`] = nextErrors[`free_pick_slot_${i}`]
            ? `${nextErrors[`free_pick_slot_${i}`]} Add a meeting link for hybrid / online.`
            : lab + "Meeting link is required when online / hybrid is enabled.";
        }
        if (isUnitCourse(selectedCourse)) {
          const idx = parseInt(s.unit_component_index, 10);
          const comps = selectedCourse.session_components || [];
          if (!Number.isInteger(idx) || idx < 0 || idx >= comps.length) {
            nextErrors[`free_pick_slot_${i}`] = lab + "Select which unit session this slot is for.";
          }
        }
        if (s.repeat_weekly) {
          const rw = parseInt(String(s.repeat_weeks ?? ""), 10);
          const hasLegacyUntil =
            String(s.repeat_until_date || "").trim() !== "" && String(s.date || "").trim() !== "";
          if ((!Number.isInteger(rw) || rw < 1) && !hasLegacyUntil) {
            nextErrors[`free_pick_slot_${i}`] =
              nextErrors[`free_pick_slot_${i}`] || lab + "Number of weeks must be at least 1.";
          }
        }
      });
      if (roomSet.size > 1) {
        nextErrors.free_pick_classroom = "All sessions must use the same classroom.";
      }
      if (isUnitCourse(selectedCourse) && slots.length > 0) {
        const comps = selectedCourse.session_components || [];
        const partMsgs = [];
        comps.forEach((comp, j) => {
          const target = unitPartTotalTargetMinutes(comp);
          if (target == null) return;
          const used = freePickExpandedMinutesForUnitPart(slots, j, null);
          if (Math.round(used) !== Math.round(target)) {
            const name = comp.session_type || comp.sessionType || `Part ${j + 1}`;
            partMsgs.push(
              `“${name}”: total scheduled time must equal ${formatMinutesHuman(target)} (${target} min); currently ${formatMinutesHuman(used)} (${used} min), including all repeats.`
            );
          }
        });
        if (partMsgs.length > 0) {
          nextErrors.free_pick_unit_budget = partMsgs.join(" ");
        }
      }
      if (!isUnitCourse(selectedCourse) && slots.length > 0) {
        const courseTotal = parseCourseDurationMinutes(selectedCourse?.duration);
        const expandedTotal = totalExpandedFreePickMinutes(slots);
        if (courseTotal != null && expandedTotal !== courseTotal) {
          nextErrors.free_pick_slots = `Total scheduled time must equal course duration (${courseTotal} minutes). Current total: ${expandedTotal} minutes.`;
        }
      }
      return nextErrors;
    }

    if (!unitAll) {
      if (!Number.isInteger(dur) || dur < 1) nextErrors.duration_minutes = "Duration must be at least 1 minute.";
    }

    if (data.schedule_type === "recurring") {
      const t = (data.recurrence_start_time || "").trim();
      if (!t) nextErrors.recurrence_start_time = "Start time is required.";
    } else if (unitAll) {
      const comps = selectedCourse.session_components || [];
      const starts = Array.isArray(data.unit_session_starts) ? data.unit_session_starts : [];
      comps.forEach((comp, i) => {
        const iso = (starts[i] || "").trim();
        if (!iso) {
          nextErrors[`unit_slot_${i}`] = `Start required for ${comp?.session_type || comp?.sessionType || "session"}.`;
          return;
        }
        const startDt = parseAsUtc(iso);
        if (!startDt || Number.isNaN(startDt.getTime())) {
          nextErrors[`unit_slot_${i}`] = "Invalid start date and time.";
        }
      });
    } else if (!(data.start_datetime || "").trim()) {
      nextErrors.start_datetime = "Start date and time is required.";
    }

    if (!unitAll && isUnitCourse(selectedCourse)) {
      const idx = parseInt(data.unit_component_index, 10);
      const comps = selectedCourse.session_components || [];
      const comp = Number.isInteger(idx) && idx >= 0 && idx < comps.length ? comps[idx] : null;
      const expected = unitComponentDurationMinutes(comp);
      if (expected != null && Number.isInteger(dur) && dur > 0 && dur !== expected) {
        nextErrors.duration_minutes = `Duration must be ${expected} minutes for this unit session (${comp?.session_type || comp?.sessionType || "part"}).`;
      }
    } else if (!isUnitCourse(selectedCourse)) {
      const courseTotalMinutes = parseCourseDurationMinutes(selectedCourse?.duration);
      if (courseTotalMinutes != null && Number.isInteger(dur) && dur > 0) {
        let plannedMinutes = dur;
        if (data.schedule_type === "recurring") {
          const slots = generateRecurrenceOccurrences({
            recurrence_start_date: data.recurrence_start_date,
            recurrence_end_date: data.recurrence_end_date,
            recurrence_frequency: data.recurrence_frequency,
            recurrence_days_of_week: data.recurrence_days_of_week,
            recurrence_start_time: data.recurrence_start_time || "09:00",
            duration_minutes: dur,
          });
          plannedMinutes = slots.length * dur;
        }
        if (plannedMinutes !== courseTotalMinutes) {
          nextErrors.duration_minutes = `Total scheduled time must equal course duration (${courseTotalMinutes} minutes). Current total: ${plannedMinutes} minutes.`;
        }
      }
    }

    return nextErrors;
  };

  const applyStepErrors = (step, stepErrors) => {
    const stepFields = STEP_FIELDS[step] || [];
    setErrors((prev) => {
      const next = { ...prev };
      stepFields.forEach((f) => delete next[f]);
      if (step === 2) {
        Object.keys(next).forEach((k) => {
          if (
            k.startsWith("unit_slot_") ||
            k.startsWith("free_pick_slot_") ||
            k.startsWith("free_pick_unit_budget") ||
            k === "unit_structure"
          ) {
            delete next[k];
          }
        });
      }
      if (step === 3) {
        Object.keys(next).forEach((k) => {
          if (
            k.startsWith("unit_inst_") ||
            k === "unit_instances" ||
            k === "unit_instances_budget" ||
            k === "unit_week1_monday"
          ) {
            delete next[k];
          }
        });
      }
      return { ...next, ...stepErrors };
    });
  };

  const hasVisibleError = (field) => {
    if (field === "unit_structure") {
      if (!stepAttempted[2]) return false;
      const e = errors.unit_structure;
      if (Array.isArray(e)) return e.length > 0;
      return Boolean(e);
    }
    if (field === "unit_instances_budget") {
      if (!stepAttempted[3]) return false;
      const v = errors.unit_instances_budget;
      if (Array.isArray(v)) return v.length > 0;
      return Boolean(v);
    }
    if (!errors[field]) return false;
    if (String(field).startsWith("unit_slot_") || String(field).startsWith("free_pick_slot_")) return stepAttempted[2];
    if (String(field).startsWith("unit_inst_") || field === "unit_instances" || field === "unit_instances_budget" || field === "unit_week1_monday")
      return stepAttempted[3];
    if (STEP_FIELDS[1].includes(field)) return stepAttempted[1];
    if (STEP_FIELDS[2].includes(field)) return stepAttempted[2];
    if (STEP_FIELDS[3].includes(field)) return unitSelectionMode ? false : stepAttempted[3];
    return true;
  };

  const goToStep = (step) => setCurrentStep(step);

  const handleNext = () => {
    setStepAttempted((prev) => ({ ...prev, [currentStep]: true }));
    if (currentStep === 1) {
      const stepErrors = validateStep1();
      applyStepErrors(1, stepErrors);
      if (Object.keys(stepErrors).length > 0) return;
    }
    if (currentStep === 2) {
      const stepErrors = validateStep2();
      applyStepErrors(2, stepErrors);
      if (Object.keys(stepErrors).length > 0) return;
    }
    if (currentStep === 3 && unitSelectionMode) {
      const stepErrors = validateUnitInstancesStep();
      applyStepErrors(3, stepErrors);
      if (Object.keys(stepErrors).length > 0) return;
    }
    if (currentStep < totalSteps) setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (isSubmitting || optionsLoading) return;
    setStepAttempted((prev) => ({ ...prev, 1: true, 2: true, 3: true, 4: true, 5: true }));

    const step1Errors = validateStep1();
    const step2Errors = validateStep2();
    const step3InstErrors = unitSelectionMode ? validateUnitInstancesStep() : {};
    applyStepErrors(1, step1Errors);
    applyStepErrors(2, step2Errors);
    if (unitSelectionMode) applyStepErrors(3, step3InstErrors);
    if (Object.keys(step1Errors).length > 0) {
      setCurrentStep(1);
      return;
    }
    if (Object.keys(step2Errors).length > 0) {
      setCurrentStep(2);
      return;
    }
    if (unitSelectionMode && Object.keys(step3InstErrors).length > 0) {
      setCurrentStep(3);
      return;
    }

    setApiError("");
    setIsSubmitting(true);
    setRecurringCreatedCount(0);
    setRecurringSkippedCount(0);
    setUnitAllSuccessCount(0);
    setFreePickSuccessCount(0);
    let createdSuccessfully = false;
    try {
      const durationMin = Number(formData.duration_minutes) || 60;

      if (formData.schedule_type === "free_pick") {
        const slots = Array.isArray(formData.free_pick_slots) ? formData.free_pick_slots : [];
        const expanded = slots
          .flatMap(expandFreePickSlotToScheduled)
          .sort((a, b) => String(a.start_datetime).localeCompare(String(b.start_datetime)));
        const roomId = slots.length ? Number(String(slots[0].classroom_id || "").trim()) : null;
        const firstHybridLink = String(slots[0]?.slot_meeting_link || "").trim();
        const selectedCourseSubmit = (options.courses || []).find((c) => String(c.id) === String(formData.course_id));
        const eventTypeForPayload = isUnitCourse(selectedCourseSubmit)
          ? "unit"
          : String(formData.event_type || "class").toLowerCase();
        let slot0Dur = 60;
        if (expanded[0]) {
          const s0 = parseAsUtc(expanded[0].start_datetime);
          const e0 = parseAsUtc(expanded[0].end_datetime);
          if (s0 && e0 && !Number.isNaN(s0.getTime()) && !Number.isNaN(e0.getTime())) {
            slot0Dur = Math.max(1, Math.round((e0.getTime() - s0.getTime()) / 60000));
          }
        }
        const basePayload = {
          title: formData.title,
          event_type: eventTypeForPayload,
          course_id: formData.course_id ? Number(formData.course_id) : null,
          lecturer_id: formData.lecturer_id ? Number(formData.lecturer_id) : null,
          classroom_id: roomId && !Number.isNaN(roomId) ? roomId : null,
          capacity: Number(formData.capacity) || 0,
          duration_minutes: slot0Dur,
          enrollment_required: formData.enrollment_required,
          allow_waitlist: formData.allow_waitlist,
          meeting_link:
            formData.meeting_link ||
            (!(roomId && !Number.isNaN(roomId)) && firstHybridLink ? firstHybridLink : null),
          description: formData.description || null,
          recurrence_rule_id: null,
          ...(eventTypeForPayload === "unit"
            ? {
                unit_structure_rows: Array.isArray(formData.unit_structure_rows)
                  ? formData.unit_structure_rows
                  : [],
              }
            : {}),
        };
        const data = await schedulingApi.createEvent({ ...basePayload, scheduled_slots: expanded });
        if (data?.error) {
          setApiError(data.error);
          return;
        }
        setCreatedId(data.event?.id ?? "—");
        setFreePickSuccessCount(data.scheduled?.length ?? expanded.length);
        setShowSuccessModal(true);
        createdSuccessfully = true;
        toast.success(`Created 1 session with ${data.scheduled?.length ?? expanded.length} scheduled time slot(s).`);
        return;
      }

      if (formData.schedule_type === SCHEDULE_TYPE_UNIT_SELECTION) {
        const slots = buildScheduledSlotsForUnitSelection(formData.unit_week1_monday, formData.unit_selection_instances);
        const selectedCourseSubmit = (options.courses || []).find((c) => String(c.id) === String(formData.course_id));
        const comps = selectedCourseSubmit?.session_components || [];
        const totalDm = comps.reduce((acc, c) => acc + (unitComponentDurationMinutes(c) || 0), 0) || durationMin;
        const derivedCap = computeCapacityFromUnitInstances(formData.unit_selection_instances, options.classrooms);
        const basePayload = {
          title: formData.title,
          event_type: "unit",
          course_id: formData.course_id ? Number(formData.course_id) : null,
          lecturer_id: null,
          classroom_id: null,
          capacity: derivedCap,
          duration_minutes: totalDm,
          enrollment_required: false,
          allow_waitlist: false,
          meeting_link: null,
          description: null,
          recurrence_rule_id: null,
          unit_structure_rows: Array.isArray(formData.unit_structure_rows)
            ? formData.unit_structure_rows
            : [],
        };
        const data = await schedulingApi.createEvent({ ...basePayload, scheduled_slots: slots });
        if (data?.error) {
          setApiError(data.error);
          return;
        }
        setCreatedId(data.event?.id ?? "—");
        setUnitAllSuccessCount(data.scheduled?.length ?? slots.length);
        setShowSuccessModal(true);
        createdSuccessfully = true;
        toast.success(`Created unit session with ${data.scheduled?.length ?? slots.length} scheduled instance(s).`);
        return;
      }

      if (formData.schedule_type === "recurring" && (formData.recurrence_frequency || "").trim()) {
        if (formData.unit_schedule_all) {
          setApiError("Recurring is not available when scheduling all unit parts at once.");
          setIsSubmitting(false);
          return;
        }
        const rulePayload = { frequency: formData.recurrence_frequency.trim().toLowerCase() };
        const endDate = (formData.recurrence_end_date || "").trim();
        if (endDate) rulePayload.end_date = endDate;
        if (rulePayload.frequency === "weekly" && Array.isArray(formData.recurrence_days_of_week) && formData.recurrence_days_of_week.length > 0) {
          rulePayload.days_of_week = formData.recurrence_days_of_week.slice().sort((a, b) => a - b);
        }
        const ruleRes = await schedulingApi.createRecurrenceRule(rulePayload);
        if (ruleRes?.error) {
          setApiError(ruleRes.error);
          setIsSubmitting(false);
          return;
        }
        const recurrence_rule_id = ruleRes.recurrence_rule?.id ?? null;
        const occurrences = generateRecurrenceOccurrences({
          recurrence_start_date: formData.recurrence_start_date,
          recurrence_end_date: formData.recurrence_end_date,
          recurrence_frequency: formData.recurrence_frequency,
          recurrence_days_of_week: formData.recurrence_days_of_week,
          recurrence_start_time: formData.recurrence_start_time || "09:00",
          duration_minutes: durationMin,
        });
        let created = 0;
        let skipped = 0;
        const basePayload = {
          title: formData.title,
          event_type: formData.event_type,
          course_id: formData.course_id ? Number(formData.course_id) : null,
          lecturer_id: formData.lecturer_id ? Number(formData.lecturer_id) : null,
          classroom_id: formData.classroom_id ? Number(formData.classroom_id) : null,
          capacity: Number(formData.capacity) || 0,
          duration_minutes: durationMin,
          enrollment_required: formData.enrollment_required,
          allow_waitlist: formData.allow_waitlist,
          meeting_link: formData.meeting_link || null,
          description: formData.description || null,
          recurrence_rule_id,
        };
        const scheduled_slots = occurrences.map((slot) => ({
          start_datetime: slot.start_datetime,
          end_datetime: slot.end_datetime,
          lecturer_id: formData.lecturer_id ? Number(formData.lecturer_id) : null,
        }));
        const res = await schedulingApi.createEvent({ ...basePayload, scheduled_slots });
        if (res?.error) {
          setApiError(res.error);
          return;
        }
        created = res.scheduled?.length ?? 0;
        setCreatedId(res.event?.id ?? "—");
        setRecurringCreatedCount(created);
        setRecurringSkippedCount(0);
        setShowSuccessModal(true);
        createdSuccessfully = true;
        if (created) toast.success(`Created 1 session with ${created} scheduled instance(s).`);
      } else {
        const selectedCourseSubmit = (options.courses || []).find((c) => String(c.id) === String(formData.course_id));
        if (isUnitCourse(selectedCourseSubmit) && formData.unit_schedule_all) {
          const comps = selectedCourseSubmit.session_components || [];
          const starts = Array.isArray(formData.unit_session_starts) ? formData.unit_session_starts : [];
          const lecturers = Array.isArray(formData.unit_session_lecturers) ? formData.unit_session_lecturers : [];
          const scheduled_slots = comps.map((comp, i) => {
            const iso = (starts[i] || "").trim();
            const startDt = parseAsUtc(iso);
            const dm = unitComponentDurationMinutes(comp) || 60;
            const endDt = startDt && !Number.isNaN(startDt.getTime()) ? new Date(startDt.getTime() + dm * 60000) : null;
            const lidRaw = lecturers[i];
            const lid =
              lidRaw != null && String(lidRaw).trim() !== "" ? Number(lidRaw) : null;
            return {
              start_datetime: startDt && !Number.isNaN(startDt.getTime()) ? startDt.toISOString().slice(0, 19) : iso,
              end_datetime: endDt ? endDt.toISOString().slice(0, 19) : "",
              unit_component_index: i,
              lecturer_id: lid != null && !Number.isNaN(lid) ? lid : null,
            };
          });
          const totalDm = comps.reduce((acc, c) => acc + (unitComponentDurationMinutes(c) || 0), 0) || durationMin;
          const basePayload = {
            title: formData.title,
            event_type: "unit",
            course_id: formData.course_id ? Number(formData.course_id) : null,
            lecturer_id: formData.lecturer_id ? Number(formData.lecturer_id) : null,
            classroom_id: formData.classroom_id ? Number(formData.classroom_id) : null,
            capacity: Number(formData.capacity) || 0,
            duration_minutes: totalDm,
            enrollment_required: formData.enrollment_required,
            allow_waitlist: formData.allow_waitlist,
            meeting_link: formData.meeting_link || null,
            description: formData.description || null,
            recurrence_rule_id: null,
            unit_structure_rows: Array.isArray(formData.unit_structure_rows)
              ? formData.unit_structure_rows
              : [],
          };
          const data = await schedulingApi.createEvent({ ...basePayload, scheduled_slots });
          if (data?.error) {
            setApiError(data.error);
            return;
          }
          setCreatedId(data.event?.id ?? "—");
          setUnitAllSuccessCount(data.scheduled?.length ?? scheduled_slots.length);
          setShowSuccessModal(true);
          createdSuccessfully = true;
          toast.success(`Scheduled ${data.scheduled?.length ?? scheduled_slots.length} unit session(s) in one event.`);
        } else {
          const startDt = parseAsUtc(formData.start_datetime);
          const endDt = startDt ? new Date(startDt.getTime() + durationMin * 60000) : null;
          const endDatetime = endDt ? endDt.toISOString().slice(0, 19) : "";
          const payload = {
            ...formData,
            course_id: formData.course_id ? Number(formData.course_id) : null,
            lecturer_id: formData.lecturer_id ? Number(formData.lecturer_id) : null,
            classroom_id: formData.classroom_id ? Number(formData.classroom_id) : null,
            capacity: Number(formData.capacity) || 0,
            duration_minutes: durationMin,
            end_datetime: endDatetime,
            recurrence_rule_id: null,
          };
          const data = await schedulingApi.createEvent(payload);
          if (data?.error) {
            setApiError(data.error);
            return;
          }
          setCreatedId(data.event?.id ?? "—");
          setShowSuccessModal(true);
          createdSuccessfully = true;
          toast.success("Session scheduled.");
        }
      }
    } catch (err) {
      setApiError(err?.message || "Failed to create session.");
    } finally {
      if (createdSuccessfully) clearScheduleCreatorDraft();
      setIsSubmitting(false);
    }
  };

  const resetWizard = () => {
    clearScheduleCreatorDraft();
    setFormData(getDefaultFormData());
    setErrors({});
    setStepAttempted({ 1: false, 2: false, 3: false, 4: false, 5: false });
    setCurrentStep(1);
    setShowSuccessModal(false);
    setCreatedId("");
    setRecurringCreatedCount(0);
    setRecurringSkippedCount(0);
    setUnitAllSuccessCount(0);
    setFreePickSuccessCount(0);
    setFreePickModalShow(false);
    setFreePickDraft(null);
    setFreePickCalendarMonth(startOfMonth(new Date()));
    setUnitInstModalShow(false);
    setUnitInstDraft(null);
    setUnitInstGroupKeys(null);
    setUnitStructureErrorAlertDismissed(false);
    setUnitInstancesBudgetAlertDismissed(false);
  };

  const renderFieldError = (field) =>
    hasVisibleError(field) ? (
      <div className={styles.errorText} role="alert">
        {Array.isArray(errors[field])
          ? errors[field].map((x) => (typeof x === "object" && x?.message ? x.message : String(x))).join(" ")
          : errors[field]}
      </div>
    ) : null;

  /** Step 2 unit structure: structured items { component_index, message } or legacy strings. */
  const unitStructureErrorItems = useMemo(() => {
    const raw = errors.unit_structure;
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw
        .map((x) =>
          typeof x === "string"
            ? { component_index: null, message: x }
            : { component_index: x?.component_index ?? null, message: x?.message || "" }
        )
        .filter((x) => x.message);
    }
    return [{ component_index: null, message: String(raw) }];
  }, [errors.unit_structure]);

  const unitStructureErrorRowSet = useMemo(() => {
    const s = new Set();
    unitStructureErrorItems.forEach((x) => {
      if (x.component_index != null && Number.isInteger(x.component_index)) s.add(x.component_index);
    });
    return s;
  }, [unitStructureErrorItems]);

  const unitStructureWeekRangeIssue = useMemo(
    () =>
      unitStructureErrorItems.some(
        (x) =>
          /week range|unit length/i.test(x.message) || /within 1 and \d+/i.test(x.message)
      ),
    [unitStructureErrorItems]
  );

  /** Place sessions step: structure/budget validation messages (array or legacy string). */
  const unitInstancesBudgetItems = useMemo(() => {
    const v = errors.unit_instances_budget;
    if (!v) return [];
    if (Array.isArray(v)) return v.filter(Boolean);
    const s = String(v).trim();
    return s ? [s] : [];
  }, [errors.unit_instances_budget]);

  const unitStructureErrorSig = useMemo(
    () => unitStructureErrorItems.map((x) => x.message).join("\n"),
    [unitStructureErrorItems]
  );
  useEffect(() => {
    setUnitStructureErrorAlertDismissed(false);
  }, [unitStructureErrorSig]);

  const unitInstancesBudgetSig = useMemo(() => unitInstancesBudgetItems.join("\n"), [unitInstancesBudgetItems]);
  useEffect(() => {
    setUnitInstancesBudgetAlertDismissed(false);
  }, [unitInstancesBudgetSig]);

  const unitInstSaveAllowed = useMemo(() => {
    if (!unitInstDraft) return false;
    if (unitInstDraft.is_online) {
      return String(unitInstDraft.slot_meeting_link || "").trim().length > 0;
    }
    if (!String(unitInstDraft.classroom_id || "").trim()) return false;
    if (unitInstDraft.hybrid_online_enabled) {
      return String(unitInstDraft.slot_meeting_link || "").trim().length > 0;
    }
    return true;
  }, [unitInstDraft]);

  const getCourseName = (id) => (options.courses || []).find((c) => c.id === Number(id))?.name ?? "—";
  const getUnitSessionReview = () => {
    const c = (options.courses || []).find((x) => String(x.id) === String(formData.course_id));
    if (!isUnitCourse(c)) return null;
    if (formData.schedule_type === SCHEDULE_TYPE_UNIT_SELECTION) return "Unit selection (structure + timetable grid)";
    if (formData.schedule_type === "free_pick") return "Chosen per slot (step 2)";
    if (formData.unit_schedule_all) {
      const n = (c.session_components || []).length;
      return n ? `All ${n} parts` : "—";
    }
    const idx = parseInt(formData.unit_component_index, 10);
    const comp = Number.isInteger(idx) && idx >= 0 ? c.session_components[idx] : null;
    return comp ? unitComponentLabel(comp) : "—";
  };
  const getLecturerName = (id) => {
    const u = (options.lecturers || []).find((u) => u.id === Number(id));
    return u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || "—" : "—";
  };
  const getClassroomCode = (id) =>
    (options.classrooms || []).find((r) => String(r.id) === String(id))?.room_code ?? "—";

  const coursesForEventType = useMemo(
    () => filterCoursesByScheduleEventType(options.courses, formData.event_type),
    [options.courses, formData.event_type]
  );
  const openFreePickForDay = useCallback(
    (day) => {
      const dateStr = formatDateYMD(day);
      const unit = isUnitCourse(selectedCourseForWizard);
      setFreePickDraft({
        key: `fp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        date: dateStr,
        start_time: "09:00",
        end_time: "10:00",
        classroom_id: String(formData.classroom_id || ""),
        unit_component_index: unit ? "0" : "",
        lecturer_id: unit ? lecturerIdFromStructureRow(formData.unit_structure_rows, 0) : String(formData.lecturer_id || ""),
        repeat_weekly: false,
        repeat_weeks: 1,
        hybrid_online_enabled: false,
        slot_meeting_link: "",
      });
      setFreePickModalShow(true);
    },
    [selectedCourseForWizard, formData.classroom_id, formData.lecturer_id, formData.unit_structure_rows]
  );

  const openFreePickEdit = useCallback((slot) => {
    const s = {
      ...slot,
      hybrid_online_enabled: Boolean(
        slot.hybrid_online_enabled || String(slot.slot_meeting_link || "").trim().length > 0
      ),
      slot_meeting_link: slot.slot_meeting_link != null ? String(slot.slot_meeting_link) : "",
    };
    if (s.repeat_weekly) {
      const rw = parseInt(String(s.repeat_weeks ?? ""), 10);
      if (!Number.isInteger(rw) || rw < 1) {
        s.repeat_weeks = freePickRepeatWeekCount(s);
      }
    } else {
      s.repeat_weeks = parseInt(String(s.repeat_weeks ?? "1"), 10);
      if (!Number.isInteger(s.repeat_weeks) || s.repeat_weeks < 1) s.repeat_weeks = 1;
    }
    setFreePickDraft(s);
    setFreePickModalShow(true);
  }, []);

  const removeFreePickSlot = useCallback((key) => {
    setFormData((prev) => ({
      ...prev,
      free_pick_slots: (prev.free_pick_slots || []).filter((s) => s.key !== key),
    }));
  }, []);

  const freePickUnitBudgetUi = useMemo(() => {
    if (!freePickDraft || !isUnitCourse(selectedCourseForWizard)) return null;
    const partIdx = parseInt(String(freePickDraft.unit_component_index ?? ""), 10);
    const comps = selectedCourseForWizard?.session_components || [];
    const comp =
      Number.isInteger(partIdx) && partIdx >= 0 && partIdx < comps.length ? comps[partIdx] : null;
    if (!comp) {
      return { kind: "pick_part" };
    }
    const target = unitPartTotalTargetMinutes(comp);
    const others = freePickExpandedMinutesForUnitPart(formData.free_pick_slots, partIdx, freePickDraft.key);
    const draftMins = freePickDraftExpandedMinutes(freePickDraft);
    const totalProjected = others + draftMins;
    const occCount = expandFreePickSlotToScheduled(freePickDraft).length;
    return {
      kind: "ok",
      comp,
      target,
      others,
      draftMins,
      totalProjected,
      remaining: target != null ? target - totalProjected : null,
      overBudget: target != null && totalProjected > target,
      shortAfterSave: target != null && totalProjected < target ? target - totalProjected : 0,
      occCount,
    };
  }, [freePickDraft, selectedCourseForWizard, formData.free_pick_slots]);

  const saveFreePickDraft = useCallback(() => {
    if (!freePickDraft) return;
    if (freePickDraft.hybrid_online_enabled && !String(freePickDraft.slot_meeting_link || "").trim()) {
      toast.error("Enter a Zoom or meeting link when hybrid / online link is enabled.");
      return;
    }
    if (isUnitCourse(selectedCourseForWizard)) {
      const partIdx = parseInt(String(freePickDraft.unit_component_index || ""), 10);
      const comp =
        Number.isInteger(partIdx) && partIdx >= 0
          ? selectedCourseForWizard?.session_components?.[partIdx]
          : null;
      const target = unitPartTotalTargetMinutes(comp);
      if (target != null && comp) {
        const others = freePickExpandedMinutesForUnitPart(formData.free_pick_slots, partIdx, freePickDraft.key);
        const draftMins = freePickDraftExpandedMinutes(freePickDraft);
        if (others + draftMins > target) {
          toast.error(
            `This would exceed the total required for this unit session type (${formatMinutesHuman(target)}, including repeats).`
          );
          return;
        }
      }
    }
    setFormData((prev) => {
      const list = [...(prev.free_pick_slots || [])];
      const ix = list.findIndex((s) => s.key === freePickDraft.key);
      const row = { ...freePickDraft };
      if (!row.hybrid_online_enabled || !String(row.slot_meeting_link || "").trim()) {
        row.hybrid_online_enabled = false;
        row.slot_meeting_link = "";
      } else {
        row.slot_meeting_link = String(row.slot_meeting_link || "").trim();
      }
      if (row.repeat_weekly) {
        const rw = parseInt(String(row.repeat_weeks ?? "1"), 10);
        row.repeat_weeks =
          Number.isInteger(rw) && rw >= 1 ? Math.min(rw, FREE_PICK_MAX_REPEAT_WEEKS) : 1;
      } else {
        row.repeat_weeks = 1;
      }
      if (ix >= 0) list[ix] = row;
      else list.push(row);
      list.sort(
        (a, b) =>
          String(a.date).localeCompare(String(b.date)) || String(a.start_time).localeCompare(String(b.start_time))
      );
      const capRoom =
        row.classroom_id != null && String(row.classroom_id).trim() !== ""
          ? (options.classrooms || []).find((r) => String(r.id) === String(row.classroom_id))
          : null;
      return {
        ...prev,
        free_pick_slots: list,
        classroom_id: String(row.classroom_id || "").trim() || prev.classroom_id,
        capacity: capRoom?.capacity ?? prev.capacity,
      };
    });
    setFreePickModalShow(false);
    setFreePickDraft(null);
    setErrors((e) => {
      const next = { ...e, free_pick_slots: null, free_pick_classroom: null, free_pick_unit_budget: null };
      Object.keys(next).forEach((k) => {
        if (k.startsWith("free_pick_slot_")) delete next[k];
      });
      return next;
    });
  }, [freePickDraft, options.classrooms, formData.free_pick_slots, selectedCourseForWizard]);

  const patchUnitStructureRow = useCallback((componentIndex, patch) => {
    setFormData((prev) => {
      const rows = [...(prev.unit_structure_rows || [])];
      const ix = rows.findIndex((r) => r.component_index === componentIndex);
      if (ix < 0) return prev;
      rows[ix] = { ...rows[ix], ...patch };
      return { ...prev, unit_structure_rows: rows };
    });
    setErrors((e) => ({ ...e, unit_structure: null }));
  }, []);

  /** After blur, coerce empty/invalid unit structure cells to safe defaults (min 1; length default 60 min). */
  const blurNormalizeUnitStructureRow = useCallback((componentIndex) => {
    setFormData((prev) => {
      const rows = [...(prev.unit_structure_rows || [])];
      const ix = rows.findIndex((r) => r.component_index === componentIndex);
      if (ix < 0) return prev;
      const r = { ...rows[ix] };
      const fixMin1 = (k) => {
        const v = r[k];
        if (v == null || v === "" || !Number.isFinite(Number(v)) || Number(v) < 1) r[k] = 1;
        else r[k] = Math.floor(Number(v));
      };
      fixMin1("week_start");
      fixMin1("week_end");
      fixMin1("times_per_week");
      let m = r.minutes_per_instance;
      if (m == null || m === "" || !Number.isFinite(Number(m)) || Number(m) < 1) {
        m = 60;
      } else {
        m = Math.max(1, Math.round(Number(m)));
      }
      r.minutes_per_instance = m;
      r.hours_per_instance = Math.round((m / 60) * 100) / 100;
      rows[ix] = r;
      return { ...prev, unit_structure_rows: rows };
    });
  }, []);

  const openNewUnitInstance = useCallback(
    (componentIndex, dayOfWeek) => {
      setUnitInstGroupKeys(null);
      const row = (formData.unit_structure_rows || []).find((r) => r.component_index === componentIndex);
      const ws = row ? Math.max(1, parseInt(String(row.week_start), 10) || 1) : 1;
      const mins = row ? Math.max(1, parseInt(String(row.minutes_per_instance), 10) || 60) : 60;
      const st = "09:00";
      const en = addMinutesToHmTime(st, mins);
      setUnitInstDraft({
        key: `ui-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        component_index: componentIndex,
        day_of_week: dayOfWeek,
        week_number: ws,
        start_time: st,
        end_time: en,
        is_online: false,
        hybrid_online_enabled: false,
        slot_capacity: "",
        slot_meeting_link: "",
        classroom_id: "",
        lecturer_id: lecturerIdFromStructureRow(formData.unit_structure_rows, componentIndex),
      });
      setUnitInstModalShow(true);
    },
    [formData.unit_structure_rows]
  );

  const openEditUnitInstance = useCallback(
    (inst) => {
      setUnitInstGroupKeys(keysForLogicalUnitSlotGroup(formData.unit_selection_instances, inst));
      const link = inst.slot_meeting_link != null ? String(inst.slot_meeting_link) : "";
      setUnitInstDraft({
        ...inst,
        hybrid_online_enabled: Boolean(
          inst.hybrid_online_enabled || (!inst.is_online && link.trim().length > 0)
        ),
        slot_meeting_link: link,
      });
      setUnitInstModalShow(true);
    },
    [formData.unit_selection_instances]
  );

  const saveUnitInstDraft = useCallback(() => {
    if (!unitInstDraft) return;
    if (!unitInstSaveAllowed) return;
    setFormData((prev) => {
      let list = [...(prev.unit_selection_instances || [])];
      const ix = list.findIndex((s) => s.key === unitInstDraft.key);
      const row = { ...unitInstDraft };
      if (!row.lecturer_id || String(row.lecturer_id).trim() === "") {
        const fromPlan = lecturerIdFromStructureRow(prev.unit_structure_rows, row.component_index);
        if (fromPlan) row.lecturer_id = fromPlan;
      }
      if (row.is_online) {
        row.hybrid_online_enabled = false;
        row.classroom_id = "";
      } else if (!row.hybrid_online_enabled || !String(row.slot_meeting_link || "").trim()) {
        row.hybrid_online_enabled = false;
        row.slot_meeting_link = "";
      } else {
        row.slot_meeting_link = String(row.slot_meeting_link || "").trim();
      }
      if (ix >= 0) {
        const keysToPatch =
          unitInstGroupKeys && unitInstGroupKeys.length > 0 ? unitInstGroupKeys : [row.key];
        list = list.map((s) => {
          if (!keysToPatch.includes(s.key)) return s;
          return {
            ...s,
            start_time: row.start_time,
            end_time: row.end_time,
            is_online: row.is_online,
            hybrid_online_enabled: row.hybrid_online_enabled,
            classroom_id: row.classroom_id,
            slot_meeting_link: row.slot_meeting_link,
            slot_capacity: row.slot_capacity,
            lecturer_id: row.lecturer_id,
          };
        });
      } else {
        const sr = (prev.unit_structure_rows || []).find(
          (r) => parseInt(String(r.component_index), 10) === parseInt(String(row.component_index), 10)
        );
        const ws = Math.max(1, parseInt(String(sr?.week_start), 10) || parseInt(String(row.week_number), 10) || 1);
        const we = Math.max(ws, parseInt(String(sr?.week_end), 10) || ws);
        for (let w = ws; w <= we; w += 1) {
          const clone = { ...row, key: `ui-${Date.now()}-${Math.random().toString(36).slice(2, 9)}-${w}`, week_number: w };
          const exists = list.some(
            (x) =>
              parseInt(String(x.component_index), 10) === parseInt(String(clone.component_index), 10) &&
              parseInt(String(x.day_of_week), 10) === parseInt(String(clone.day_of_week), 10) &&
              parseInt(String(x.week_number), 10) === parseInt(String(clone.week_number), 10) &&
              String(x.start_time) === String(clone.start_time) &&
              String(x.end_time) === String(clone.end_time) &&
              Boolean(x.is_online) === Boolean(clone.is_online) &&
              Boolean(x.hybrid_online_enabled) === Boolean(clone.hybrid_online_enabled) &&
              String(x.slot_capacity || "") === String(clone.slot_capacity || "") &&
              String(x.slot_meeting_link || "") === String(clone.slot_meeting_link || "") &&
              String(x.classroom_id || "") === String(clone.classroom_id || "") &&
              String(x.lecturer_id || "") === String(clone.lecturer_id || "")
          );
          if (!exists) list.push(clone);
        }
      }
      return { ...prev, unit_selection_instances: list };
    });
    setUnitInstModalShow(false);
    setUnitInstDraft(null);
    setUnitInstGroupKeys(null);
    setErrors((e) => {
      const next = { ...e, unit_instances: null, unit_instances_budget: null, unit_week1_monday: null };
      Object.keys(next).forEach((k) => {
        if (k.startsWith("unit_inst_")) delete next[k];
      });
      return next;
    });
  }, [unitInstDraft, unitInstSaveAllowed, unitInstGroupKeys]);

  const removeUnitLogicalSlotGroup = useCallback((repInst) => {
    setFormData((prev) => {
      const drop = new Set(keysForLogicalUnitSlotGroup(prev.unit_selection_instances, repInst));
      return {
        ...prev,
        unit_selection_instances: (prev.unit_selection_instances || []).filter((s) => !drop.has(s.key)),
      };
    });
  }, []);

  if (optionsLoading) {
    return (
      <Container fluid className={styles.page}>
        <PageLayout
          title="Create Session"
          subtitle="Step-by-step: name it, set time and place, then optional extras. You can go back any time before you submit."
          backTo="/schedule"
        >
          <div className={`${styles.wizardWrap} ${styles.wizardWrapFullWidth}`}>
            <Card className={`${styles.wizardCard} bg-transparent border-0 shadow-none`}>
              <Card.Body className={styles.cardBody}>
                <div className="d-flex align-items-center justify-content-center py-5">
                  <Spinner animation="border" />
                  <span className="ms-2">Loading courses, rooms, and lecturers…</span>
                </div>
              </Card.Body>
            </Card>
          </div>
        </PageLayout>
      </Container>
    );
  }

  return (
    <Container fluid className={styles.page}>
      <PageLayout
            title="Create Session"
            subtitle="Step-by-step: name it, set time and place, then optional extras. You can go back any time before you submit."
            backTo="/schedule"
          >
            <div className={`${styles.wizardWrap} ${styles.wizardWrapFullWidth}`}>
              <Card className={`${styles.wizardCard} bg-transparent border-0 shadow-none`}>
                <Card.Body className={styles.cardBody}>
                  <header className={styles.gwHeader}>
                    <div className={styles.gwProgressIndicator}>
                      <div className={styles.gwProgressText} id="create-event-step-heading">
                        Step {currentStep} of {totalSteps}
                        {currentStepMeta?.title ? (
                          <>
                            <span className={styles.gwProgressSep} aria-hidden="true">
                              {" "}
                              ·{" "}
                            </span>
                            <span className={styles.gwProgressStepName}>{currentStepMeta.title}</span>
                          </>
                        ) : null}
                      </div>
                      <ProgressBar
                        now={progressPercent}
                        className={styles.gwLinearProgress}
                        aria-labelledby="create-event-step-heading"
                      />
                    </div>
                  </header>

                <nav className={styles.gwNav} aria-label="Wizard steps">
                  <div className={styles.gwProgress}>
                    {stepMeta.map((step, idx) => {
                      const stepNumber = idx + 1;
                      const status = stepNumber < currentStep ? "done" : stepNumber === currentStep ? "current" : "locked";
                      const stepClass = status === "done" ? styles.gwStepDone : status === "current" ? styles.gwStepCurrent : styles.gwStepLocked;
                      const badgeClass = status === "done" ? styles.gwStepBadgeDone : status === "current" ? styles.gwStepBadgeCurrent : styles.gwStepBadgeLocked;
                      const shortClass = status === "done" ? styles.gwStepShortDone : status === "current" ? styles.gwStepShortCurrent : styles.gwStepShortLocked;
                      return (
                        <button
                          key={step.id}
                          type="button"
                          className={`${styles.gwStep} ${stepClass}`}
                          onClick={() => goToStep(stepNumber)}
                          disabled={stepNumber > currentStep}
                          aria-label={`Go to step ${stepNumber}: ${step.title}`}
                        >
                          <div className={`${styles.gwStepBadge} ${badgeClass}`}>
                            {status === "done" ? <CheckCircleRoundedIcon fontSize="small" /> : step.icon}
                          </div>
                          <div className={styles.gwStepLabel}>
                            <div className={`${styles.gwStepShort} ${shortClass}`}>{step.short}</div>
                            <div className={styles.gwStepTitle}>{step.title}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </nav>

                {apiError && <Alert variant="danger">{apiError}</Alert>}

                <Form onSubmit={(e) => e.preventDefault()} noValidate>
                  <section key={currentStep} className={styles.stepBody} aria-live="polite">
                    {currentStep === 1 && (
                      <Row className="g-3">
                        <Col md={12}>
                          <Form.Group controlId="title">
                            <Form.Label>Title *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.title}
                              onChange={(e) => setFieldValue("title", e.target.value)}
                              isInvalid={hasVisibleError("title")}
                              placeholder="e.g. IELTS Writing Workshop — Group A"
                            />
                            {renderFieldError("title")}
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="event_type">
                            <Form.Label>Type</Form.Label>
                            {selectedCourseIsUnit ? (
                              <>
                                <Form.Select value="unit" disabled className="small">
                                  <option value="unit">Unit</option>
                                </Form.Select>
                                <Form.Text className="text-muted">Sessions for a unit program course are always type Unit.</Form.Text>
                              </>
                            ) : (
                              <Form.Select
                                value={formData.event_type}
                                onChange={(e) => setFieldValue("event_type", e.target.value)}
                                isInvalid={hasVisibleError("event_type")}
                              >
                                {SCHEDULE_EVENT_TYPES.map((t) => (
                                  <option key={t} value={t}>
                                    {t === "unit" ? "Unit" : t.charAt(0).toUpperCase() + t.slice(1)}
                                  </option>
                                ))}
                              </Form.Select>
                            )}
                            {!selectedCourseIsUnit && renderFieldError("event_type")}
                          </Form.Group>
                        </Col>
                        {!selectedCourseIsUnit && (
                          <Col md={6}>
                            <Form.Group controlId="schedule_type">
                              <Form.Label>How should this meet?</Form.Label>
                              <div className="d-flex gap-3 align-items-center flex-wrap">
                                <Form.Check
                                  type="radio"
                                  id="schedule_one_time"
                                  name="schedule_type"
                                  label="Single session"
                                  checked={formData.schedule_type === "one_time"}
                                  onChange={() => setFieldValue("schedule_type", "one_time")}
                                />
                                <Form.Check
                                  type="radio"
                                  id="schedule_free_pick"
                                  name="schedule_type"
                                  label="Pick dates on calendar"
                                  checked={formData.schedule_type === "free_pick"}
                                  onChange={() => setFieldValue("schedule_type", "free_pick")}
                                  disabled={formData.unit_schedule_all}
                                  title={formData.unit_schedule_all ? "Turn off “schedule all unit parts” to use free pick." : undefined}
                                />
                              </div>
                            </Form.Group>
                          </Col>
                        )}
                        <Col md={6}>
                          <Form.Group controlId="course_id">
                            <Form.Label>Course *</Form.Label>
                            <Form.Select
                              value={formData.course_id}
                              onChange={(e) => setFieldValue("course_id", e.target.value)}
                              isInvalid={hasVisibleError("course_id")}
                            >
                              <option value="">—</option>
                              {coursesForEventType.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </Form.Select>
                            {coursesForEventType.length === 0 && (
                              <Form.Text className="text-warning">No courses match this session type. Change type above or add courses in Programs.</Form.Text>
                            )}
                            {renderFieldError("course_id")}
                          </Form.Group>
                        </Col>
                        {selectedCourseIsUnit && formData.schedule_type !== SCHEDULE_TYPE_UNIT_SELECTION && (
                          <>
                            <Col md={12}>
                              <Form.Group controlId="unit_schedule_all">
                                <Form.Check
                                  type="checkbox"
                                  id="unit_schedule_all"
                                  label="Schedule all unit parts at once"
                                  checked={!!formData.unit_schedule_all}
                                  onChange={(e) => setFieldValue("unit_schedule_all", e.target.checked)}
                                  disabled={formData.schedule_type === "free_pick"}
                                  title={
                                    formData.schedule_type === "free_pick"
                                      ? "Switch to one-time to schedule all unit parts in one step."
                                      : undefined
                                  }
                                />
                                <Form.Text className="text-muted d-block">
                                  {formData.unit_schedule_all
                                    ? "In step 2, set a start time for each part. They are stored as one event with multiple slots. Recurring is not available in this mode."
                                    : "Or leave unchecked and pick a single part below (or use Free pick to choose a part per date)."}
                                </Form.Text>
                              </Form.Group>
                            </Col>
                            {!formData.unit_schedule_all && formData.schedule_type !== "free_pick" && (
                              <Col md={12}>
                                <Form.Group controlId="unit_component_index">
                                  <Form.Label>Unit session *</Form.Label>
                                  <Form.Select
                                    value={formData.unit_component_index}
                                    onChange={(e) => setFieldValue("unit_component_index", e.target.value)}
                                    isInvalid={hasVisibleError("unit_component_index")}
                                  >
                                    <option value="">Select which part of the unit this calendar entry is for…</option>
                                    {(selectedCourseForWizard.session_components || []).map((comp, idx) => (
                                      <option key={idx} value={String(idx)}>
                                        {unitComponentLabel(comp)}
                                      </option>
                                    ))}
                                  </Form.Select>
                                  {renderFieldError("unit_component_index")}
                                </Form.Group>
                              </Col>
                            )}
                          </>
                        )}
                        {!selectedCourseIsUnit && (
                          <Col md={6}>
                            <Form.Group controlId="lecturer_id">
                              <Form.Label>Lecturer</Form.Label>
                              <Form.Select value={formData.lecturer_id} onChange={(e) => setFieldValue("lecturer_id", e.target.value)}>
                                <option value="">—</option>
                                {(options.lecturers || []).map((u) => (
                                  <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        )}
                      </Row>
                    )}

                    {currentStep === 2 && unitSelectionMode && selectedCourseForWizard && (
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>How many weeks is this unit? *</Form.Label>
                            <Form.Control
                              type="number"
                              min={1}
                              value={formData.unit_total_weeks == null || formData.unit_total_weeks === "" ? "" : formData.unit_total_weeks}
                              onChange={(e) => {
                                const v = intFromOptionalInput(e.target.value);
                                setFormData((prev) => ({ ...prev, unit_total_weeks: v }));
                                setErrors((er) => ({ ...er, unit_structure: null }));
                              }}
                              onBlur={() => {
                                setFormData((prev) => ({
                                  ...prev,
                                  unit_total_weeks:
                                    prev.unit_total_weeks == null ||
                                    prev.unit_total_weeks === "" ||
                                    !Number.isFinite(Number(prev.unit_total_weeks)) ||
                                    Number(prev.unit_total_weeks) < 1
                                      ? 4
                                      : Math.max(1, Math.floor(Number(prev.unit_total_weeks))),
                                }));
                              }}
                              isInvalid={stepAttempted[2] && unitStructureWeekRangeIssue}
                            />
                            {stepAttempted[2] && unitStructureWeekRangeIssue ? (
                              <Form.Control.Feedback type="invalid" className="d-block">
                                Check this number matches the last week you use in the table (each row’s last week must be ≤ this).
                              </Form.Control.Feedback>
                            ) : null}
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          {hasVisibleError("unit_structure") &&
                          unitStructureErrorItems.length > 0 &&
                          !unitStructureErrorAlertDismissed ? (
                            <Alert
                              variant="danger"
                              dismissible
                              onClose={() => setUnitStructureErrorAlertDismissed(true)}
                              className={`mb-3 border-0 shadow-sm ${styles.unitStructureErrorAlert}`}
                            >
                              <Alert.Heading as="h3" className="h6 mb-2">
                                Fix your weekly plan to continue
                              </Alert.Heading>
                              <ul className="mb-0 ps-3">
                                {unitStructureErrorItems.map((item, idx) => (
                                  <li key={idx} className="mb-2">
                                    {item.message}
                                  </li>
                                ))}
                              </ul>
                              <p className={`mb-0 mt-2 small ${styles.unitStructureErrorFootnote}`}>
                                Problem rows are highlighted in the table.
                              </p>
                            </Alert>
                          ) : null}
                          <div className="table-responsive">
                            <table className="table table-sm align-middle">
                              <thead>
                                <tr>
                                  <th>Session type</th>
                                  <th>Lecturer</th>
                                  <th>First week</th>
                                  <th>Last week</th>
                                  <th>Sessions per week</th>
                                  <th>Hours</th>
                                  <th>Min</th>
                                  <th>Planned total</th>
                                  <th>Course target</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(formData.unit_structure_rows || []).map((row) => {
                                  const comp = selectedCourseForWizard.session_components[row.component_index];
                                  const target = unitComponentDurationMinutes(comp);
                                  const got = structureRowTargetMinutes(row);
                                  const ok = target == null || Math.round(got) === Math.round(target);
                                  const rowHasError =
                                    stepAttempted[2] && unitStructureErrorRowSet.has(row.component_index);
                                  return (
                                    <tr
                                      key={row.component_index}
                                      className={rowHasError ? styles.unitStructureRowError : undefined}
                                    >
                                      <td>{comp ? unitComponentLabel(comp) : "—"}</td>
                                      <td style={{ minWidth: 160 }}>
                                        <Form.Select
                                          size="sm"
                                          value={row.lecturer_id != null ? String(row.lecturer_id) : ""}
                                          onChange={(e) =>
                                            patchUnitStructureRow(row.component_index, {
                                              lecturer_id: e.target.value,
                                            })
                                          }
                                          aria-label={`Lecturer for ${comp ? unitComponentLabel(comp) : "session"}`}
                                        >
                                          <option value="">— Select —</option>
                                          {(options.lecturers || []).map((u) => (
                                            <option key={u.id} value={String(u.id)}>
                                              {u.first_name} {u.last_name}
                                            </option>
                                          ))}
                                        </Form.Select>
                                        <Form.Text className="text-muted small d-block mt-1">
                                          Default for this session type; override per slot in step 3.
                                        </Form.Text>
                                      </td>
                                      <td style={{ maxWidth: 88 }}>
                                        <Form.Control
                                          type="number"
                                          min={1}
                                          size="sm"
                                          value={row.week_start == null || row.week_start === "" ? "" : row.week_start}
                                          isInvalid={rowHasError}
                                          onChange={(e) =>
                                            patchUnitStructureRow(row.component_index, {
                                              week_start: intFromOptionalInput(e.target.value),
                                            })
                                          }
                                          onBlur={() => blurNormalizeUnitStructureRow(row.component_index)}
                                        />
                                      </td>
                                      <td style={{ maxWidth: 88 }}>
                                        <Form.Control
                                          type="number"
                                          min={1}
                                          size="sm"
                                          value={row.week_end == null || row.week_end === "" ? "" : row.week_end}
                                          isInvalid={rowHasError}
                                          onChange={(e) =>
                                            patchUnitStructureRow(row.component_index, {
                                              week_end: intFromOptionalInput(e.target.value),
                                            })
                                          }
                                          onBlur={() => blurNormalizeUnitStructureRow(row.component_index)}
                                        />
                                      </td>
                                      <td style={{ maxWidth: 88 }}>
                                        <Form.Control
                                          type="number"
                                          min={1}
                                          size="sm"
                                          value={row.times_per_week == null || row.times_per_week === "" ? "" : row.times_per_week}
                                          isInvalid={rowHasError}
                                          onChange={(e) =>
                                            patchUnitStructureRow(row.component_index, {
                                              times_per_week: intFromOptionalInput(e.target.value),
                                            })
                                          }
                                          onBlur={() => blurNormalizeUnitStructureRow(row.component_index)}
                                        />
                                      </td>
                                      <td style={{ maxWidth: 72 }}>
                                        <Form.Control
                                          type="number"
                                          min={0}
                                          size="sm"
                                          aria-label="Hours per class"
                                          value={
                                            row.minutes_per_instance == null || row.minutes_per_instance === ""
                                              ? ""
                                              : splitTotalMinutesToHm(row.minutes_per_instance).hours
                                          }
                                          isInvalid={rowHasError}
                                          onChange={(e) => {
                                            const raw = e.target.value;
                                            if (raw === "") {
                                              const mmOnly =
                                                row.minutes_per_instance == null
                                                  ? 0
                                                  : Math.min(
                                                      59,
                                                      Math.max(0, splitTotalMinutesToHm(row.minutes_per_instance).minutes)
                                                    );
                                              if (mmOnly < 1) {
                                                patchUnitStructureRow(row.component_index, {
                                                  minutes_per_instance: null,
                                                  hours_per_instance: null,
                                                });
                                              } else {
                                                patchUnitStructureRow(row.component_index, {
                                                  minutes_per_instance: mmOnly,
                                                  hours_per_instance: Math.round((mmOnly / 60) * 100) / 100,
                                                });
                                              }
                                              return;
                                            }
                                            const h = intFromOptionalInput(raw);
                                            if (h == null) return;
                                            const mm =
                                              row.minutes_per_instance == null
                                                ? 0
                                                : Math.min(
                                                    59,
                                                    Math.max(0, splitTotalMinutesToHm(row.minutes_per_instance).minutes)
                                                  );
                                            let total = Math.max(0, h) * 60 + mm;
                                            if (total < 1) {
                                              patchUnitStructureRow(row.component_index, {
                                                minutes_per_instance: null,
                                                hours_per_instance: null,
                                              });
                                              return;
                                            }
                                            patchUnitStructureRow(row.component_index, {
                                              minutes_per_instance: total,
                                              hours_per_instance: Math.round((total / 60) * 100) / 100,
                                            });
                                          }}
                                          onBlur={() => blurNormalizeUnitStructureRow(row.component_index)}
                                        />
                                      </td>
                                      <td style={{ maxWidth: 72 }}>
                                        <Form.Control
                                          type="number"
                                          min={0}
                                          max={59}
                                          size="sm"
                                          aria-label="Minutes per class"
                                          value={
                                            row.minutes_per_instance == null || row.minutes_per_instance === ""
                                              ? ""
                                              : splitTotalMinutesToHm(row.minutes_per_instance).minutes
                                          }
                                          isInvalid={rowHasError}
                                          onChange={(e) => {
                                            const raw = e.target.value;
                                            if (raw === "") {
                                              const hhOnly =
                                                row.minutes_per_instance == null
                                                  ? 0
                                                  : Math.max(0, splitTotalMinutesToHm(row.minutes_per_instance).hours);
                                              if (hhOnly < 1) {
                                                patchUnitStructureRow(row.component_index, {
                                                  minutes_per_instance: null,
                                                  hours_per_instance: null,
                                                });
                                              } else {
                                                const total = hhOnly * 60;
                                                patchUnitStructureRow(row.component_index, {
                                                  minutes_per_instance: total,
                                                  hours_per_instance: Math.round((total / 60) * 100) / 100,
                                                });
                                              }
                                              return;
                                            }
                                            let mm = intFromOptionalInput(raw);
                                            if (mm == null) return;
                                            mm = Math.min(59, Math.max(0, mm));
                                            const hh =
                                              row.minutes_per_instance == null
                                                ? 0
                                                : Math.max(0, splitTotalMinutesToHm(row.minutes_per_instance).hours);
                                            let total = hh * 60 + mm;
                                            if (total < 1) {
                                              patchUnitStructureRow(row.component_index, {
                                                minutes_per_instance: null,
                                                hours_per_instance: null,
                                              });
                                              return;
                                            }
                                            patchUnitStructureRow(row.component_index, {
                                              minutes_per_instance: total,
                                              hours_per_instance: Math.round((total / 60) * 100) / 100,
                                            });
                                          }}
                                          onBlur={() => blurNormalizeUnitStructureRow(row.component_index)}
                                        />
                                      </td>
                                      <td className={!ok && stepAttempted[2] ? "fw-semibold text-danger" : undefined}>
                                        <span className="text-nowrap">{formatDurationHmLabel(got)}</span>
                                        <span className="text-muted small ms-1">({got} min)</span>
                                      </td>
                                      <td className={ok ? "text-success" : stepAttempted[2] ? "text-danger fw-semibold" : "text-danger"}>
                                        {target != null ? (
                                          <>
                                            <span className="text-nowrap">{formatDurationHmLabel(target)}</span>
                                            <span className="text-muted small ms-1">({target} min)</span>
                                          </>
                                        ) : (
                                          "—"
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </Col>
                      </Row>
                    )}

                    {currentStep === 2 && !unitSelectionMode && (
                      <Row className="g-3">
                        <Col md={12}>
                          <Form.Group controlId="classroom_id">
                            <Form.Label>{formData.schedule_type === "free_pick" ? "Default classroom" : "Classroom"}</Form.Label>
                            <Form.Select value={formData.classroom_id} onChange={(e) => setFieldValue("classroom_id", e.target.value)}>
                              <option value="">—</option>
                              {(options.classrooms || []).map((r) => (
                                <option key={r.id} value={r.id}>{r.room_code} ({r.capacity} seats)</option>
                              ))}
                            </Form.Select>
                            {formData.schedule_type === "free_pick" && (
                              <Form.Text className="text-muted d-block">
                                Pre-filled when you add a slot from the calendar. Every session row must use the same room.
                              </Form.Text>
                            )}
                          </Form.Group>
                        </Col>
                        {formData.schedule_type === "free_pick" ? (
                          <>
                            <Col md={12}>
                              {renderFieldError("free_pick_slots")}
                              {renderFieldError("free_pick_classroom")}
                              {renderFieldError("free_pick_unit_budget")}
                              <ThemeProvider theme={freePickCalendarTheme}>
                                <CalendarMonthGrid
                                  calendarMonth={freePickCalendarMonth}
                                  onMonthChange={(delta) =>
                                    setFreePickCalendarMonth((m) => startOfMonth(addMonths(m, delta)))
                                  }
                                  onDayClick={openFreePickForDay}
                                  eventsForDay={(d) => {
                                    const ymd = formatDateYMD(d);
                                    const course = selectedCourseForWizard;
                                    const showUnit = isUnitCourse(course);
                                    return (formData.free_pick_slots || [])
                                      .filter((s) => freePickSlotOccurrenceDates(s).includes(ymd))
                                      .map((s) => {
                                        const idx = parseInt(s.unit_component_index, 10);
                                        const comp =
                                          showUnit && Number.isInteger(idx) && course?.session_components
                                            ? course.session_components[idx]
                                            : null;
                                        return {
                                          id: `${s.key}__${ymd}`,
                                          title: `${s.start_time}–${s.end_time}`,
                                          subtitle: showUnit ? unitComponentShortName(comp, idx) : undefined,
                                          color: freePickCalendarColorForSlot(showUnit, s.unit_component_index),
                                        };
                                      });
                                  }}
                                  renderEvent={(ev) => (
                                    <Typography
                                      variant="caption"
                                      component="div"
                                      sx={{
                                        fontSize: "0.62rem",
                                        lineHeight: 1.25,
                                        bgcolor: alpha(ev.color || FREE_PICK_NON_UNIT_COLOR, 0.22),
                                        color: "text.primary",
                                        px: 0.5,
                                        py: 0.25,
                                        borderRadius: 1,
                                        borderLeft: "3px solid",
                                        borderColor: ev.color || FREE_PICK_NON_UNIT_COLOR,
                                        minWidth: 0,
                                      }}
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          fontWeight: 700,
                                          display: "block",
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {ev.title}
                                      </Box>
                                      {ev.subtitle ? (
                                        <Box
                                          component="span"
                                          sx={{
                                            display: "block",
                                            fontWeight: 600,
                                            opacity: 0.92,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                          }}
                                        >
                                          {ev.subtitle}
                                        </Box>
                                      ) : null}
                                    </Typography>
                                  )}
                                  minDayHeight={88}
                                />
                              </ThemeProvider>
                            </Col>
                            <Col md={12}>
                              <div className="fw-semibold mb-2">Sessions</div>
                              {(formData.free_pick_slots || []).length === 0 ? (
                                <p className="text-muted small mb-0">Click a future date on the calendar to add a session.</p>
                              ) : (
                                <div className="table-responsive">
                                  <table className="table table-sm align-middle">
                                    <thead>
                                      <tr>
                                        <th>Date</th>
                                        <th>Time</th>
                                        <th>Lecturer</th>
                                        <th>Room</th>
                                        <th>Online link</th>
                                        {selectedCourseIsUnit && <th>Unit part</th>}
                                        <th>Repeat</th>
                                        <th className="text-end">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(formData.free_pick_slots || []).map((s) => {
                                        const idx = parseInt(s.unit_component_index, 10);
                                        const comp =
                                          selectedCourseIsUnit && Number.isInteger(idx) && selectedCourseForWizard?.session_components
                                            ? selectedCourseForWizard.session_components[idx]
                                            : null;
                                        const wkCount = freePickRepeatWeekCount(s);
                                        return (
                                          <tr key={s.key}>
                                            <td>{s.date}</td>
                                            <td>
                                              {s.start_time} – {s.end_time}
                                            </td>
                                            <td>{getLecturerName(s.lecturer_id)}</td>
                                            <td>{getClassroomCode(s.classroom_id)}</td>
                                            <td className="small">
                                              {String(s.slot_meeting_link || "").trim() ? (
                                                <a
                                                  href={String(s.slot_meeting_link).trim()}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-truncate d-inline-block"
                                                  style={{ maxWidth: 180 }}
                                                  title={String(s.slot_meeting_link).trim()}
                                                >
                                                  {String(s.slot_meeting_link).trim().length > 36
                                                    ? `${String(s.slot_meeting_link).trim().slice(0, 36)}…`
                                                    : String(s.slot_meeting_link).trim()}
                                                </a>
                                              ) : (
                                                "—"
                                              )}
                                            </td>
                                            {selectedCourseIsUnit && <td>{comp ? unitComponentFreePickLabel(comp) : "—"}</td>}
                                            <td className="small">
                                              {s.repeat_weekly
                                                ? `Weekly × ${wkCount} week${wkCount === 1 ? "" : "s"}`
                                                : "—"}
                                            </td>
                                            <td className="text-end text-nowrap">
                                              <Button
                                                type="button"
                                                variant="link"
                                                size="sm"
                                                className="p-0 me-2"
                                                onClick={() => openFreePickEdit(s)}
                                              >
                                                Edit
                                              </Button>
                                              <Button
                                                type="button"
                                                variant="link"
                                                size="sm"
                                                className="p-0 text-danger"
                                                onClick={() => removeFreePickSlot(s.key)}
                                              >
                                                Remove
                                              </Button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              {(formData.free_pick_slots || []).map((_, i) =>
                                hasVisibleError(`free_pick_slot_${i}`) ? (
                                  <div key={`fpe-${i}`} className={styles.errorText} role="alert">
                                    {errors[`free_pick_slot_${i}`]}
                                  </div>
                                ) : null
                              )}
                            </Col>
                          </>
                        ) : formData.schedule_type === "recurring" ? (
                          <>
                            <Col md={6}>
                              <Form.Group controlId="recurrence_start_time">
                                <Form.Label>Start time *</Form.Label>
                                <Form.Control
                                  type="time"
                                  value={formData.recurrence_start_time || "09:00"}
                                  onChange={(e) => setFieldValue("recurrence_start_time", e.target.value)}
                                  isInvalid={hasVisibleError("recurrence_start_time")}
                                />
                                <Form.Text className="text-muted">Same time on each date (dates from step 1).</Form.Text>
                                {renderFieldError("recurrence_start_time")}
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group controlId="duration_minutes">
                                <Form.Label>Duration (minutes) *</Form.Label>
                                <Form.Control
                                  type="number"
                                  min={1}
                                  value={
                                    formData.duration_minutes == null || formData.duration_minutes === ""
                                      ? ""
                                      : formData.duration_minutes
                                  }
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    if (raw === "") {
                                      setFieldValue("duration_minutes", null);
                                      return;
                                    }
                                    const n = Number(raw);
                                    setFieldValue("duration_minutes", Number.isFinite(n) ? n : null);
                                  }}
                                  onBlur={() => {
                                    setFormData((prev) => {
                                      const dm = prev.duration_minutes;
                                      const nextDm =
                                        dm == null ||
                                        dm === "" ||
                                        !Number.isFinite(Number(dm)) ||
                                        Number(dm) < 1
                                          ? 60
                                          : Math.max(1, Number(dm));
                                      const next = { ...prev, duration_minutes: nextDm };
                                      if (next.start_datetime) {
                                        const start = parseAsUtc(next.start_datetime);
                                        if (start) {
                                          next.end_datetime = new Date(
                                            start.getTime() + nextDm * 60000
                                          ).toISOString().slice(0, 19);
                                        }
                                      }
                                      return next;
                                    });
                                  }}
                                  isInvalid={hasVisibleError("duration_minutes")}
                                  readOnly={selectedCourseIsUnit}
                                  disabled={selectedCourseIsUnit}
                                />
                                <Form.Text className="text-muted">
                                  {selectedCourseIsUnit
                                    ? "Set from the unit session in step 1."
                                    : "Each occurrence is checked for clashes (lecturer, room) before being created."}
                                </Form.Text>
                                {renderFieldError("duration_minutes")}
                              </Form.Group>
                            </Col>
                          </>
                        ) : selectedCourseIsUnit && formData.unit_schedule_all ? (
                          <>
                            <Col md={12}>
                              <Form.Text className="text-muted d-block mb-2">
                                Each part uses the duration from the unit definition. Set every start time, or enter the first and use “Chain back-to-back”.
                              </Form.Text>
                              {(selectedCourseForWizard.session_components || []).map((comp, idx) => (
                                <Form.Group key={idx} className="mb-3" controlId={`unit_slot_${idx}`}>
                                  <Form.Label>
                                    {unitComponentLabel(comp)} — start *
                                  </Form.Label>
                                  <Form.Control
                                    type="datetime-local"
                                    value={isoUtcToDatetimeLocalValue((formData.unit_session_starts || [])[idx] || "")}
                                    onChange={(e) => updateUnitSessionStart(idx, e.target.value || "")}
                                    isInvalid={hasVisibleError(`unit_slot_${idx}`)}
                                  />
                                  <Form.Group className="mt-2" controlId={`unit_slot_lecturer_${idx}`}>
                                    <Form.Label className="small mb-1">Lecturer</Form.Label>
                                    <Form.Select
                                      size="sm"
                                      value={(formData.unit_session_lecturers || [])[idx] || ""}
                                      onChange={(e) => updateUnitSessionLecturer(idx, e.target.value)}
                                    >
                                      <option value="">— Select —</option>
                                      {(options.lecturers || []).map((u) => (
                                        <option key={u.id} value={String(u.id)}>
                                          {u.first_name} {u.last_name}
                                        </option>
                                      ))}
                                    </Form.Select>
                                  </Form.Group>
                                  {hasVisibleError(`unit_slot_${idx}`) && (
                                    <div className={styles.errorText} role="alert">
                                      {errors[`unit_slot_${idx}`]}
                                    </div>
                                  )}
                                </Form.Group>
                              ))}
                              <Button type="button" variant="outline-secondary" size="sm" onClick={chainUnitSessionStarts}>
                                Chain back-to-back from first start
                              </Button>
                            </Col>
                          </>
                        ) : (
                          <>
                            <Col md={6}>
                              <Form.Group controlId="start_datetime">
                                <Form.Label>Start date & time *</Form.Label>
                                <Form.Control
                                  type="datetime-local"
                                  value={isoUtcToDatetimeLocalValue(formData.start_datetime)}
                                  onChange={(e) => setFieldValue("start_datetime", e.target.value || "")}
                                  isInvalid={hasVisibleError("start_datetime")}
                                />
                                {renderFieldError("start_datetime")}
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group controlId="duration_minutes">
                                <Form.Label>Duration (minutes) *</Form.Label>
                                <Form.Control
                                  type="number"
                                  min={1}
                                  value={
                                    formData.duration_minutes == null || formData.duration_minutes === ""
                                      ? ""
                                      : formData.duration_minutes
                                  }
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    if (raw === "") {
                                      setFieldValue("duration_minutes", null);
                                      return;
                                    }
                                    const n = Number(raw);
                                    setFieldValue("duration_minutes", Number.isFinite(n) ? n : null);
                                  }}
                                  onBlur={() => {
                                    setFormData((prev) => {
                                      const dm = prev.duration_minutes;
                                      const nextDm =
                                        dm == null ||
                                        dm === "" ||
                                        !Number.isFinite(Number(dm)) ||
                                        Number(dm) < 1
                                          ? 60
                                          : Math.max(1, Number(dm));
                                      const next = { ...prev, duration_minutes: nextDm };
                                      if (next.start_datetime) {
                                        const start = parseAsUtc(next.start_datetime);
                                        if (start) {
                                          next.end_datetime = new Date(
                                            start.getTime() + nextDm * 60000
                                          ).toISOString().slice(0, 19);
                                        }
                                      }
                                      return next;
                                    });
                                  }}
                                  isInvalid={hasVisibleError("duration_minutes")}
                                  readOnly={selectedCourseIsUnit}
                                  disabled={selectedCourseIsUnit}
                                />
                                <Form.Text className="text-muted">
                                  {selectedCourseIsUnit
                                    ? "Set from the unit session in step 1."
                                    : "End time is set automatically from start + duration."}
                                </Form.Text>
                                {renderFieldError("duration_minutes")}
                              </Form.Group>
                            </Col>
                            {formData.start_datetime &&
                              formData.duration_minutes != null &&
                              formData.duration_minutes !== "" &&
                              Number(formData.duration_minutes) >= 1 && (
                              <Col md={12}>
                                <Form.Group>
                                  <Form.Label className="text-muted small">End (auto)</Form.Label>
                                  <Form.Control
                                    plaintext
                                    readOnly
                                    value={formData.end_datetime && parseAsUtc(formData.end_datetime) ? parseAsUtc(formData.end_datetime).toLocaleString() : "—"}
                                    className="small"
                                  />
                                </Form.Group>
                              </Col>
                            )}
                          </>
                        )}
                        <Col md={6}>
                          <Form.Group controlId="capacity">
                            <Form.Label>Capacity (0 = no limit)</Form.Label>
                            <Form.Control
                              type="number"
                              min={0}
                              value={formData.capacity}
                              onChange={(e) => setFieldValue("capacity", e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    {currentStep === 3 && unitSelectionMode && selectedCourseForWizard && (
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Week 1 starts (Monday) *</Form.Label>
                            <Form.Control
                              type="date"
                              value={formData.unit_week1_monday || ""}
                              onChange={(e) => {
                                setFormData((prev) => ({ ...prev, unit_week1_monday: e.target.value }));
                                setErrors((er) => ({ ...er, unit_week1_monday: null }));
                              }}
                              isInvalid={hasVisibleError("unit_week1_monday")}
                            />
                            {renderFieldError("unit_week1_monday")}
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          {renderFieldError("unit_instances")}
                          {hasVisibleError("unit_instances_budget") &&
                          unitInstancesBudgetItems.length > 0 &&
                          !unitInstancesBudgetAlertDismissed ? (
                            <Alert
                              variant="danger"
                              dismissible
                              onClose={() => setUnitInstancesBudgetAlertDismissed(true)}
                              className={`mb-3 border-0 shadow-sm ${styles.unitStructureErrorAlert}`}
                            >
                              <Alert.Heading as="h3" className="h6 mb-2">
                                Timetable does not match your weekly plan
                              </Alert.Heading>
                              <ul className="mb-0 ps-3">
                                {unitInstancesBudgetItems.map((msg, idx) => (
                                  <li key={idx} className="mb-2">
                                    {msg}
                                  </li>
                                ))}
                              </ul>
                            </Alert>
                          ) : null}
                        </Col>
                        <Col md={12}>
                          {(selectedCourseForWizard.session_components || []).map((comp, compIdx) => {
                            const allInsts = formData.unit_selection_instances || [];
                            const row = (formData.unit_structure_rows || []).find((r) => r.component_index === compIdx);
                            const ws = row ? Math.max(1, parseInt(String(row.week_start), 10) || 1) : 1;
                            const we = row ? Math.max(ws, parseInt(String(row.week_end), 10) || ws) : 1;
                            const label = comp?.session_type || comp?.sessionType || `Part ${compIdx + 1}`;
                            const planLecturer = lecturerIdFromStructureRow(formData.unit_structure_rows, compIdx);
                            return (
                              <div key={compIdx} className="mb-4">
                                <div className="fw-semibold mb-2">
                                  {label} (week {ws} – week {we})
                                  {planLecturer ? (
                                    <span className="text-muted fw-normal small ms-2">
                                      · default lecturer: {getLecturerName(planLecturer)}
                                    </span>
                                  ) : null}
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                  {UNIT_SELECTION_DAY_LABELS.map((dlabel, day) => {
                                    const slotClusters = clusterUnitSlotsForDayColumn(allInsts, compIdx, day);
                                    return (
                                      <div
                                        key={day}
                                        className="border rounded p-2 flex-grow-1"
                                        style={{ minWidth: 120, maxWidth: 200 }}
                                      >
                                        <div className="small text-muted text-center mb-1">{dlabel}</div>
                                        <Button
                                          type="button"
                                          variant="outline-secondary"
                                          size="sm"
                                          className="w-100 mb-2"
                                          onClick={() => openNewUnitInstance(compIdx, day)}
                                        >
                                          + Add session
                                        </Button>
                                        <Stack spacing={0.5}>
                                          {slotClusters.map((slot) => {
                                            const instErrMsg = firstUnitInstErrorInGroup(
                                              allInsts,
                                              errors,
                                              stepAttempted[3],
                                              slot.rep
                                            );
                                            const lec =
                                              slot.rep.lecturer_id != null &&
                                              String(slot.rep.lecturer_id).trim() !== ""
                                                ? getLecturerName(slot.rep.lecturer_id)
                                                : null;
                                            return (
                                              <div key={slot.rep.key}>
                                                <Paper
                                                  elevation={1}
                                                  sx={{
                                                    p: 1,
                                                    cursor: "pointer",
                                                    fontSize: "0.75rem",
                                                    "&:hover": { bgcolor: "action.hover" },
                                                    ...(instErrMsg
                                                      ? {
                                                          border: "2px solid",
                                                          borderColor: "error.main",
                                                          boxSizing: "border-box",
                                                        }
                                                      : {}),
                                                  }}
                                                  onClick={() => openEditUnitInstance(slot.rep)}
                                                >
                                                  <div>
                                                    {slot.weekLabel} · {slot.rep.start_time}–{slot.rep.end_time}
                                                  </div>
                                                  <div className="text-muted">
                                                    {lec ? `${lec} · ` : ""}
                                                    {(() => {
                                                      if (slot.rep.is_online) {
                                                        return `Online · ${(slot.rep.slot_meeting_link || "").trim() ? "Zoom link set" : "link missing"}`;
                                                      }
                                                      const room = getClassroomCode(slot.rep.classroom_id);
                                                      if (slot.rep.hybrid_online_enabled) {
                                                        return String(slot.rep.slot_meeting_link || "").trim()
                                                          ? `${room} · Hybrid (link set)`
                                                          : `${room} · Hybrid · link missing`;
                                                      }
                                                      return room;
                                                    })()}
                                                    {slot.rep.slot_capacity ? ` · cap ${slot.rep.slot_capacity}` : ""}
                                                  </div>
                                                  <Button
                                                    type="button"
                                                    variant="link"
                                                    size="sm"
                                                    className="p-0 text-danger"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      removeUnitLogicalSlotGroup(slot.rep);
                                                    }}
                                                  >
                                                    Remove
                                                  </Button>
                                                </Paper>
                                                {instErrMsg ? (
                                                  <div className="text-danger small mt-1" role="alert">
                                                    {instErrMsg}
                                                  </div>
                                                ) : null}
                                              </div>
                                            );
                                          })}
                                        </Stack>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </Col>
                      </Row>
                    )}

                    {!unitSelectionMode && currentStep === 3 && (
                      <Row className="g-3">
                        <Col md={12}>
                          <Form.Group>
                            <Form.Check
                              type="checkbox"
                              id="enrollment_required"
                              label="Enrollment required"
                              checked={formData.enrollment_required}
                              onChange={(e) => setFieldValue("enrollment_required", e.target.checked)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group>
                            <Form.Check
                              type="checkbox"
                              id="allow_waitlist"
                              label="Allow waitlist"
                              checked={formData.allow_waitlist}
                              onChange={(e) => setFieldValue("allow_waitlist", e.target.checked)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group controlId="meeting_link">
                            <Form.Label>Meeting link</Form.Label>
                            <Form.Control
                              type="url"
                              value={formData.meeting_link}
                              onChange={(e) => setFieldValue("meeting_link", e.target.value)}
                              placeholder="https://..."
                            />
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group controlId="description">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={formData.description}
                              onChange={(e) => setFieldValue("description", e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    {currentStep === 4 && (
                      <div className={styles.reviewWrap}>
                        <Card className={styles.reviewCard}>
                          <Card.Body>
                            <div className={styles.reviewHeader}>
                              <h5>Basics</h5>
                              <Button type="button" variant="outline-primary" size="sm" onClick={() => goToStep(1)}>Edit</Button>
                            </div>
                            <div className={styles.reviewGrid}>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Title</span>
                                <span className={styles.reviewValue}>{formData.title || "—"}</span>
                              </div>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Type</span>
                                <span className={styles.reviewValue}>
                                  {String(formData.event_type || "").toLowerCase() === "unit"
                                    ? "Unit"
                                    : String(formData.event_type || "").charAt(0).toUpperCase() + String(formData.event_type || "").slice(1)}
                                </span>
                              </div>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Schedule</span>
                                <span className={styles.reviewValue}>
                                  {formData.schedule_type === SCHEDULE_TYPE_UNIT_SELECTION
                                    ? "Unit selection"
                                    : formData.schedule_type === "free_pick"
                                      ? "Free pick (calendar)"
                                      : formData.schedule_type === "recurring" && formData.recurrence_frequency
                                        ? `Recurring (${formData.recurrence_frequency.charAt(0).toUpperCase() + formData.recurrence_frequency.slice(1)})`
                                        : "One-time"}
                                </span>
                              </div>
                              {formData.schedule_type === "recurring" && formData.recurrence_start_date && (
                                <div className={styles.reviewItem}>
                                  <span className={styles.reviewLabel}>Recurrence start</span>
                                  <span className={styles.reviewValue}>{formData.recurrence_start_date}</span>
                                </div>
                              )}
                              {formData.schedule_type === "recurring" && formData.recurrence_end_date && (
                                <div className={styles.reviewItem}>
                                  <span className={styles.reviewLabel}>Recurrence end</span>
                                  <span className={styles.reviewValue}>{formData.recurrence_end_date}</span>
                                </div>
                              )}
                              {formData.schedule_type === "recurring" && (formData.recurrence_frequency || "").toLowerCase() === "weekly" && Array.isArray(formData.recurrence_days_of_week) && formData.recurrence_days_of_week.length > 0 && (
                                <div className={styles.reviewItem}>
                                  <span className={styles.reviewLabel}>Repeat on</span>
                                  <span className={styles.reviewValue}>
                                    {formData.recurrence_days_of_week.map((d) => DAYS_OF_WEEK.find((x) => x.value === d)?.label ?? d).join(", ")}
                                  </span>
                                </div>
                              )}
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Course</span>
                                <span className={styles.reviewValue}>{getCourseName(formData.course_id)}</span>
                              </div>
                              {selectedCourseIsUnit && !unitSelectionMode && (
                                <div className={styles.reviewItem}>
                                  <span className={styles.reviewLabel}>Unit session</span>
                                  <span className={styles.reviewValue}>{getUnitSessionReview()}</span>
                                </div>
                              )}
                              {!selectedCourseIsUnit && (
                                <div className={styles.reviewItem}>
                                  <span className={styles.reviewLabel}>Lecturer</span>
                                  <span className={styles.reviewValue}>{getLecturerName(formData.lecturer_id)}</span>
                                </div>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                        <Card className={styles.reviewCard}>
                          <Card.Body>
                            <div className={styles.reviewHeader}>
                              <h5>Schedule & Room</h5>
                              <Button type="button" variant="outline-primary" size="sm" onClick={() => goToStep(2)}>Edit</Button>
                            </div>
                            <div className={styles.reviewGrid}>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Classroom</span>
                                <span className={styles.reviewValue}>
                                  {formData.schedule_type === SCHEDULE_TYPE_UNIT_SELECTION
                                    ? "Set per instance"
                                    : getClassroomCode(formData.classroom_id)}
                                </span>
                              </div>
                              {formData.schedule_type === SCHEDULE_TYPE_UNIT_SELECTION ? (
                                <>
                                  <div className={styles.reviewItem}>
                                    <span className={styles.reviewLabel}>Unit length (weeks)</span>
                                    <span className={styles.reviewValue}>{formData.unit_total_weeks}</span>
                                  </div>
                                  <div className={styles.reviewItem}>
                                    <span className={styles.reviewLabel}>Week 1 (Monday)</span>
                                    <span className={styles.reviewValue}>{formData.unit_week1_monday || "—"}</span>
                                  </div>
                                  <div className={styles.reviewItem} style={{ gridColumn: "1 / -1" }}>
                                    <span className={styles.reviewLabel}>Placed instances</span>
                                    <span className={styles.reviewValue}>
                                      {(formData.unit_selection_instances || []).length} session(s); room or online link set per instance.
                                    </span>
                                  </div>
                                </>
                              ) : formData.schedule_type === "free_pick" ? (
                                <>
                                  <div className={styles.reviewItem} style={{ gridColumn: "1 / -1" }}>
                                    <span className={styles.reviewLabel}>Calendar sessions</span>
                                    <span className={styles.reviewValue}>
                                      {(formData.free_pick_slots || []).length === 0
                                        ? "—"
                                        : `${(formData.free_pick_slots || []).length} row(s); ${totalExpandedFreePickMinutes(formData.free_pick_slots)} min total after repeats.`}
                                    </span>
                                  </div>
                                  {(formData.free_pick_slots || []).map((s) => {
                                    const idx = parseInt(s.unit_component_index, 10);
                                    const comp =
                                      selectedCourseIsUnit && Number.isInteger(idx) && selectedCourseForWizard?.session_components
                                        ? selectedCourseForWizard.session_components[idx]
                                        : null;
                                    const rep = s.repeat_weekly
                                      ? `, weekly × ${freePickRepeatWeekCount(s)} wk`
                                      : "";
                                    return (
                                      <div key={s.key} className={styles.reviewItem} style={{ gridColumn: "1 / -1" }}>
                                        <span className={styles.reviewLabel}>{s.date}</span>
                                        <span className={styles.reviewValue}>
                                          {s.start_time}–{s.end_time}
                                          {comp ? ` · ${unitComponentFreePickLabel(comp)}` : ""}
                                          {` · ${getClassroomCode(s.classroom_id)}`}
                                          {String(s.slot_meeting_link || "").trim()
                                            ? ` · Hybrid link: ${String(s.slot_meeting_link).trim().slice(0, 56)}${
                                                String(s.slot_meeting_link).trim().length > 56 ? "…" : ""
                                              }`
                                            : ""}
                                          {rep}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </>
                              ) : formData.schedule_type === "recurring" ? (
                                <>
                                  <div className={styles.reviewItem}>
                                    <span className={styles.reviewLabel}>Start time</span>
                                    <span className={styles.reviewValue}>{formData.recurrence_start_time || "09:00"}</span>
                                  </div>
                                  <div className={styles.reviewItem}>
                                    <span className={styles.reviewLabel}>Duration</span>
                                    <span className={styles.reviewValue}>{formData.duration_minutes} min</span>
                                  </div>
                                  <div className={styles.reviewItem}>
                                    <span className={styles.reviewLabel}>Applies to</span>
                                    <span className={styles.reviewValue}>
                                      {formData.recurrence_start_date} to {formData.recurrence_end_date}
                                      {(formData.recurrence_frequency || "").toLowerCase() === "weekly" && Array.isArray(formData.recurrence_days_of_week) && formData.recurrence_days_of_week.length > 0
                                        ? ` on ${formData.recurrence_days_of_week.map((d) => DAYS_OF_WEEK.find((x) => x.value === d)?.label ?? d).join(", ")}`
                                        : ""}
                                    </span>
                                  </div>
                                </>
                              ) : selectedCourseIsUnit && formData.unit_schedule_all ? (
                                (selectedCourseForWizard.session_components || []).map((comp, idx) => {
                                  const iso = (formData.unit_session_starts || [])[idx] || "";
                                  const st = iso && parseAsUtc(iso) ? parseAsUtc(iso).toLocaleString() : "—";
                                  const dm = unitComponentDurationMinutes(comp);
                                  const endIso = iso && dm != null && parseAsUtc(iso)
                                    ? new Date(parseAsUtc(iso).getTime() + dm * 60000).toISOString().slice(0, 19)
                                    : "";
                                  const endStr = endIso && parseAsUtc(endIso) ? parseAsUtc(endIso).toLocaleString() : "—";
                                  return (
                                    <div key={idx} className={styles.reviewItem} style={{ gridColumn: "1 / -1" }}>
                                      <span className={styles.reviewLabel}>{comp?.session_type || comp?.sessionType || `Part ${idx + 1}`}</span>
                                      <span className={styles.reviewValue}>
                                        {st} → {endStr}
                                        {dm != null ? ` (${dm} min)` : ""}
                                      </span>
                                    </div>
                                  );
                                })
                              ) : (
                                <>
                                  <div className={styles.reviewItem}>
                                    <span className={styles.reviewLabel}>Start</span>
                                    <span className={styles.reviewValue}>{formData.start_datetime && parseAsUtc(formData.start_datetime) ? parseAsUtc(formData.start_datetime).toLocaleString() : "—"}</span>
                                  </div>
                                  <div className={styles.reviewItem}>
                                    <span className={styles.reviewLabel}>Duration</span>
                                    <span className={styles.reviewValue}>{formData.duration_minutes} min</span>
                                  </div>
                                  <div className={styles.reviewItem}>
                                    <span className={styles.reviewLabel}>End (auto)</span>
                                    <span className={styles.reviewValue}>{formData.end_datetime && parseAsUtc(formData.end_datetime) ? parseAsUtc(formData.end_datetime).toLocaleString() : "—"}</span>
                                  </div>
                                </>
                              )}
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Capacity</span>
                                <span className={styles.reviewValue}>
                                  {formData.schedule_type === SCHEDULE_TYPE_UNIT_SELECTION
                                    ? (() => {
                                        const n = computeCapacityFromUnitInstances(
                                          formData.unit_selection_instances,
                                          options.classrooms
                                        );
                                        return n > 0
                                          ? `${n} (max from instance room sizes and optional per-slot caps)`
                                          : "No limit (no numeric cap from placed instances)";
                                      })()
                                    : formData.capacity || "No limit"}
                                </span>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                        {!unitSelectionMode && (
                          <Card className={styles.reviewCard}>
                            <Card.Body>
                              <div className={styles.reviewHeader}>
                                <h5>Options</h5>
                                <Button type="button" variant="outline-primary" size="sm" onClick={() => goToStep(3)}>
                                  Edit
                                </Button>
                              </div>
                              <div className={styles.reviewGrid}>
                                <div className={styles.reviewItem}>
                                  <span className={styles.reviewLabel}>Enrollment required</span>
                                  <span className={styles.reviewValue}>{formData.enrollment_required ? "Yes" : "No"}</span>
                                </div>
                                <div className={styles.reviewItem}>
                                  <span className={styles.reviewLabel}>Allow waitlist</span>
                                  <span className={styles.reviewValue}>{formData.allow_waitlist ? "Yes" : "No"}</span>
                                </div>
                                <div className={styles.reviewItem}>
                                  <span className={styles.reviewLabel}>Meeting link</span>
                                  <span className={styles.reviewValue}>{formData.meeting_link || "—"}</span>
                                </div>
                                <div className={styles.reviewItem}>
                                  <span className={styles.reviewLabel}>Description</span>
                                  <span className={styles.reviewValue}>{formData.description || "—"}</span>
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        )}
                      </div>
                    )}
                  </section>

                  <div className={styles.actions}>
                    <Button type="button" variant="outline-secondary" onClick={handleBack} disabled={currentStep === 1 || isSubmitting}>
                      Back
                    </Button>
                    {currentStep < totalSteps ? (
                      <Button type="button" variant="primary" onClick={handleNext} disabled={isSubmitting}>
                        {nextStepMeta ? `Continue to ${nextStepMeta.short}` : "Continue"}
                      </Button>
                    ) : (
                      <Button variant="primary" type="button" onClick={handleSubmit} disabled={isSubmitting} className={styles.submitBtn}>
                        {isSubmitting ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            <span className={styles.loadingText}>Creating...</span>
                          </>
                        ) : (
                          "Create Session"
                        )}  
                      </Button>
                    )}
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </div>
      </PageLayout>

      <Modal show={freePickModalShow} onHide={() => { setFreePickModalShow(false); setFreePickDraft(null); }} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{freePickDraft ? `Session — ${freePickDraft.date}` : "Session"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {freePickDraft && (
            <Row className="g-3">
              <Col md={6}>
                <Form.Group controlId="fp_date">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={freePickDraft.date || ""}
                    onChange={(e) => setFreePickDraft((d) => ({ ...d, date: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="fp_start">
                  <Form.Label>Start</Form.Label>
                  <Form.Control
                    type="time"
                    value={freePickDraft.start_time || ""}
                    onChange={(e) => setFreePickDraft((d) => ({ ...d, start_time: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="fp_end">
                  <Form.Label>End</Form.Label>
                  <Form.Control
                    type="time"
                    value={freePickDraft.end_time || ""}
                    onChange={(e) => setFreePickDraft((d) => ({ ...d, end_time: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group controlId="fp_lecturer">
                  <Form.Label>Lecturer</Form.Label>
                  <Form.Select
                    value={freePickDraft.lecturer_id != null ? String(freePickDraft.lecturer_id) : ""}
                    onChange={(e) =>
                      setFreePickDraft((d) => {
                        const next = { ...d, lecturer_id: e.target.value };
                        if (selectedCourseIsUnit && e.target.value === "" && d.unit_component_index != null) {
                          const fromPlan = lecturerIdFromStructureRow(
                            formData.unit_structure_rows,
                            d.unit_component_index
                          );
                          if (fromPlan) next.lecturer_id = fromPlan;
                        }
                        return next;
                      })
                    }
                  >
                    <option value="">— Select —</option>
                    {(options.lecturers || []).map((u) => (
                      <option key={u.id} value={String(u.id)}>
                        {u.first_name} {u.last_name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Each calendar row can use a different lecturer.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group controlId="fp_classroom">
                  <Form.Label>
                    Classroom{freePickDraft.hybrid_online_enabled ? "" : " *"}
                  </Form.Label>
                  <Form.Select
                    value={freePickDraft.classroom_id || ""}
                    onChange={(e) => setFreePickDraft((d) => ({ ...d, classroom_id: e.target.value }))}
                  >
                    <option value="">—</option>
                    {(options.classrooms || []).map((r) => (
                      <option key={r.id} value={r.id}>{r.room_code} ({r.capacity} seats)</option>
                    ))}
                  </Form.Select>
                  {freePickDraft.hybrid_online_enabled && (
                    <Form.Text className="text-muted">
                      Optional for online / hybrid — leave blank for a fully online session.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Check
                  type="checkbox"
                  id="fp_hybrid_online"
                  label="Online / hybrid — add a Zoom or meeting link (classroom optional)"
                  checked={!!freePickDraft.hybrid_online_enabled}
                  onChange={(e) =>
                    setFreePickDraft((d) => ({
                      ...d,
                      hybrid_online_enabled: e.target.checked,
                      slot_meeting_link: e.target.checked ? d.slot_meeting_link || "" : "",
                    }))
                  }
                />
              </Col>
              {freePickDraft.hybrid_online_enabled && (
                <Col md={12}>
                  <Form.Group controlId="fp_slot_meeting_link">
                    <Form.Label>Meeting link *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="https://zoom.us/j/… or other join URL"
                      value={freePickDraft.slot_meeting_link || ""}
                      onChange={(e) => setFreePickDraft((d) => ({ ...d, slot_meeting_link: e.target.value }))}
                      autoComplete="off"
                    />
                    <Form.Text className="text-muted">
                      {String(freePickDraft.classroom_id || "").trim()
                        ? "Remote attendees use this link; the classroom is still used for the physical session."
                        : "Fully online session — attendees join via this link."}
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}
              {selectedCourseIsUnit && (
                <Col md={12}>
                  <Form.Group controlId="fp_unit_part">
                    <Form.Label>Unit session *</Form.Label>
                    <Form.Select
                      value={freePickDraft.unit_component_index || ""}
                      onChange={(e) => {
                        const partIdx = e.target.value;
                        const fromPlan = lecturerIdFromStructureRow(formData.unit_structure_rows, partIdx);
                        setFreePickDraft((d) => ({
                          ...d,
                          unit_component_index: partIdx,
                          lecturer_id: fromPlan || d.lecturer_id || "",
                        }));
                      }}
                    >
                      <option value="">Select part…</option>
                      {(selectedCourseForWizard.session_components || []).map((comp, idx) => (
                        <option key={idx} value={String(idx)}>
                          {unitComponentFreePickLabel(comp)}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
              {selectedCourseIsUnit && freePickUnitBudgetUi?.kind === "pick_part" && (
                <Col md={12}>
                  <Alert variant="secondary" className="py-2 small mb-0">
                    Select which unit session this is to see the required total time and how this row counts toward it
                    (including repeats).
                  </Alert>
                </Col>
              )}
              {selectedCourseIsUnit && freePickUnitBudgetUi?.kind === "ok" && (
                <Col md={12}>
                  <Alert
                    variant={freePickUnitBudgetUi.overBudget ? "danger" : "light"}
                    className="py-2 small mb-0"
                  >
                    {freePickUnitBudgetUi.target != null ? (
                      <>
                        <div>
                          <strong>Total required (from course, all meetings + repeats):</strong>{" "}
                          {formatMinutesHuman(freePickUnitBudgetUi.target)}
                        </div>
                        <div>
                          <strong>Already scheduled (other rows, incl. repeats):</strong>{" "}
                          {formatMinutesHuman(freePickUnitBudgetUi.others)}
                        </div>
                        <div>
                          <strong>
                            This row (
                            {freePickUnitBudgetUi.occCount} occurrence
                            {freePickUnitBudgetUi.occCount === 1 ? "" : "s"}, incl. repeats):
                          </strong>{" "}
                          {formatMinutesHuman(freePickUnitBudgetUi.draftMins)}
                        </div>
                        <div>
                          <strong>Gap after save (must reach zero across all rows):</strong>{" "}
                          {formatMinutesHuman(Math.max(0, freePickUnitBudgetUi.remaining ?? 0))}
                        </div>
                        {freePickUnitBudgetUi.overBudget ? (
                          <div className="mt-2 fw-semibold">
                            Over the required total for this session type — shorten times, fewer weeks, or use another
                            row less.
                          </div>
                        ) : freePickUnitBudgetUi.shortAfterSave > 0 ? (
                          <div className="mt-2 text-muted">
                            After saving, you will still need{" "}
                            <strong>{formatMinutesHuman(freePickUnitBudgetUi.shortAfterSave)}</strong> more for this
                            session type (add rows or extend repeats).
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <div className="text-muted mb-0">
                        This session type has no duration on the course. Set total contact time (hours/minutes) for it in
                        Programs.
                      </div>
                    )}
                  </Alert>
                </Col>
              )}
              <Col md={12}>
                <Form.Check
                  type="checkbox"
                  id="fp_repeat_weekly"
                  label="Repeat weekly on this weekday"
                  checked={!!freePickDraft.repeat_weekly}
                  onChange={(e) =>
                    setFreePickDraft((d) => ({
                      ...d,
                      repeat_weekly: e.target.checked,
                      repeat_weeks: e.target.checked
                        ? Math.max(1, parseInt(String(d.repeat_weeks ?? "1"), 10) || 1)
                        : d.repeat_weeks,
                    }))
                  }
                />
              </Col>
              {freePickDraft.repeat_weekly && (
                <Col md={6}>
                  <Form.Group controlId="fp_repeat_weeks">
                    <Form.Label>Number of weeks</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      max={FREE_PICK_MAX_REPEAT_WEEKS}
                      step={1}
                      value={
                        freePickDraft.repeat_weeks == null || freePickDraft.repeat_weeks === ""
                          ? ""
                          : Math.min(
                              FREE_PICK_MAX_REPEAT_WEEKS,
                              Math.max(1, parseInt(String(freePickDraft.repeat_weeks), 10) || 1)
                            )
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") {
                          setFreePickDraft((d) => ({ ...d, repeat_weeks: null }));
                          return;
                        }
                        const n = parseInt(raw, 10);
                        setFreePickDraft((d) => ({
                          ...d,
                          repeat_weeks:
                            Number.isInteger(n) && n >= 1
                              ? Math.min(FREE_PICK_MAX_REPEAT_WEEKS, n)
                              : null,
                        }));
                      }}
                      onBlur={() => {
                        setFreePickDraft((d) => {
                          if (!d.repeat_weekly) return d;
                          const rw =
                            d.repeat_weeks == null ||
                            d.repeat_weeks === "" ||
                            !Number.isFinite(Number(d.repeat_weeks)) ||
                            Number(d.repeat_weeks) < 1
                              ? 1
                              : Math.min(
                                  FREE_PICK_MAX_REPEAT_WEEKS,
                                  Math.max(1, parseInt(String(d.repeat_weeks), 10) || 1)
                                );
                          return { ...d, repeat_weeks: rw };
                        });
                      }}
                    />
                    <Form.Text className="text-muted">
                      Includes the first week ({freePickDraft.date || "—"}). Same weekday each time.
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" type="button" onClick={() => { setFreePickModalShow(false); setFreePickDraft(null); }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={saveFreePickDraft}
            disabled={
              !freePickDraft ||
              (freePickDraft.hybrid_online_enabled && !String(freePickDraft.slot_meeting_link || "").trim()) ||
              (selectedCourseIsUnit &&
                (freePickUnitBudgetUi?.kind === "pick_part" ||
                  (freePickUnitBudgetUi?.kind === "ok" && freePickUnitBudgetUi.overBudget)))
            }
          >
            Save session
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={unitInstModalShow}
        onHide={() => {
          setUnitInstModalShow(false);
          setUnitInstDraft(null);
          setUnitInstGroupKeys(null);
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Unit session instance</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {unitInstDraft && selectedCourseForWizard && (
            <Row className="g-3">
              <Col md={12}>
                <Form.Text className="text-muted">
                  {(() => {
                    const comp = selectedCourseForWizard.session_components?.[unitInstDraft.component_index];
                    const compLabel = comp?.session_type || comp?.sessionType || "Session";
                    const sr = (formData.unit_structure_rows || []).find(
                      (r) => r.component_index === unitInstDraft.component_index
                    );
                    const pws = Math.max(1, parseInt(String(sr?.week_start), 10) || 1);
                    const pwe = Math.max(pws, parseInt(String(sr?.week_end), 10) || pws);
                    const alreadySaved = (formData.unit_selection_instances || []).some(
                      (s) => s.key === unitInstDraft.key
                    );
                    let weekLine = "";
                    if (!alreadySaved) {
                      weekLine =
                        pws === pwe
                          ? `Saves for week ${pws}.`
                          : `Saves for weeks ${pws}–${pwe} (one row per week).`;
                    } else if (unitInstGroupKeys && unitInstGroupKeys.length > 1) {
                      const wks = (formData.unit_selection_instances || [])
                        .filter((s) => unitInstGroupKeys.includes(s.key))
                        .map((s) => parseInt(String(s.week_number), 10))
                        .filter(Number.isFinite)
                        .sort((a, b) => a - b);
                      const lo = wks[0];
                      const hi = wks[wks.length - 1];
                      weekLine =
                        lo === hi
                          ? `Week ${lo} — edits apply to every week in this group.`
                          : `Weeks ${lo}–${hi} — edits apply to all of them.`;
                    } else {
                      weekLine = `Week ${unitInstDraft.week_number}.`;
                    }
                    return (
                      <>
                        {compLabel} · {UNIT_SELECTION_DAY_LABELS[unitInstDraft.day_of_week] || "Day"}
                        <span className="d-block mt-1">{weekLine}</span>
                      </>
                    );
                  })()}
                </Form.Text>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Start *</Form.Label>
                  <Form.Control
                    type="time"
                    value={unitInstDraft.start_time || ""}
                    onChange={(e) => setUnitInstDraft((d) => ({ ...d, start_time: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>End *</Form.Label>
                  <Form.Control
                    type="time"
                    value={unitInstDraft.end_time || ""}
                    onChange={(e) => setUnitInstDraft((d) => ({ ...d, end_time: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Check
                  type="checkbox"
                  id="unit-inst-online"
                  label="Online only (no physical room — Zoom / meeting link required)"
                  checked={Boolean(unitInstDraft.is_online)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setUnitInstDraft((d) => ({
                      ...d,
                      is_online: checked,
                      classroom_id: checked ? "" : d.classroom_id,
                      hybrid_online_enabled: false,
                      slot_meeting_link: checked ? d.slot_meeting_link : "",
                    }));
                  }}
                />
              </Col>
              {unitInstDraft.is_online ? (
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Zoom / meeting link *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="https://zoom.us/j/…"
                      value={unitInstDraft.slot_meeting_link || ""}
                      onChange={(e) => setUnitInstDraft((d) => ({ ...d, slot_meeting_link: e.target.value }))}
                      isInvalid={!String(unitInstDraft.slot_meeting_link || "").trim()}
                    />
                    {!unitInstSaveAllowed ? (
                      <Form.Text className="text-danger">Enter a meeting link before saving.</Form.Text>
                    ) : null}
                  </Form.Group>
                </Col>
              ) : (
                <>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Classroom *</Form.Label>
                      <Form.Select
                        value={unitInstDraft.classroom_id || ""}
                        onChange={(e) => setUnitInstDraft((d) => ({ ...d, classroom_id: e.target.value }))}
                        isInvalid={!String(unitInstDraft.classroom_id || "").trim()}
                        required
                      >
                        <option value="">Select a classroom…</option>
                        {(options.classrooms || []).map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.room_code} ({r.capacity} seats)
                          </option>
                        ))}
                      </Form.Select>
                      {!unitInstDraft.classroom_id && !unitInstSaveAllowed ? (
                        <Form.Text className="text-danger">Choose a classroom before saving.</Form.Text>
                      ) : null}
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Check
                      type="checkbox"
                      id="unit-inst-hybrid"
                      label="Hybrid — add a Zoom / meeting link for remote attendees (in-person uses the classroom above)"
                      checked={!!unitInstDraft.hybrid_online_enabled}
                      onChange={(e) =>
                        setUnitInstDraft((d) => ({
                          ...d,
                          hybrid_online_enabled: e.target.checked,
                          slot_meeting_link: e.target.checked ? d.slot_meeting_link || "" : "",
                        }))
                      }
                    />
                  </Col>
                  {unitInstDraft.hybrid_online_enabled && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Meeting link *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="https://zoom.us/j/… or other join URL"
                          value={unitInstDraft.slot_meeting_link || ""}
                          onChange={(e) => setUnitInstDraft((d) => ({ ...d, slot_meeting_link: e.target.value }))}
                          isInvalid={
                            unitInstDraft.hybrid_online_enabled && !String(unitInstDraft.slot_meeting_link || "").trim()
                          }
                        />
                        <Form.Text className="text-muted">
                          Remote participants use this link; the room above is still the physical venue.
                        </Form.Text>
                        {unitInstDraft.hybrid_online_enabled && !String(unitInstDraft.slot_meeting_link || "").trim() ? (
                          <Form.Text className="text-danger">Enter a meeting link for hybrid.</Form.Text>
                        ) : null}
                      </Form.Group>
                    </Col>
                  )}
                </>
              )}
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Lecturer</Form.Label>
                  <Form.Select
                    value={unitInstDraft.lecturer_id != null ? String(unitInstDraft.lecturer_id) : ""}
                    onChange={(e) =>
                      setUnitInstDraft((d) => ({
                        ...d,
                        lecturer_id: e.target.value === "" ? "" : e.target.value,
                      }))
                    }
                  >
                    <option value="">— None —</option>
                    {(options.lecturers || []).map((u) => (
                      <option key={u.id} value={String(u.id)}>
                        {u.first_name} {u.last_name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Defaults from the weekly plan for this session type. Change here to use a different teacher for this slot (applies to every week it runs).
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>
                    Capacity for this instance{" "}
                    <span className="text-muted small fw-normal">(optional)</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    placeholder={unitInstDraft.is_online ? "Default: event capacity" : "Default: room capacity"}
                    value={unitInstDraft.slot_capacity ?? ""}
                    onChange={(e) =>
                      setUnitInstDraft((d) => ({
                        ...d,
                        slot_capacity: e.target.value === "" ? "" : e.target.value,
                      }))
                    }
                  />
                  <Form.Text className="text-muted">
                    Leave blank to use the room&apos;s seat count (in person) or the event capacity (online). Allocations cannot exceed this per slot.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            type="button"
            onClick={() => {
              setUnitInstModalShow(false);
              setUnitInstDraft(null);
              setUnitInstGroupKeys(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={saveUnitInstDraft}
            disabled={!unitInstDraft || !unitInstSaveAllowed}
          >
            Save instance
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSuccessModal} onHide={resetWizard} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {freePickSuccessCount > 0
              ? "Sessions scheduled"
              : unitAllSuccessCount > 0
                ? "Unit sessions scheduled"
                : recurringCreatedCount > 0 || recurringSkippedCount > 0
                  ? "Recurring sessions"
                  : "Session created"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.successModalBody}>
          <div className={styles.successIcon} aria-hidden="true" />
          {freePickSuccessCount > 0 ? (
            <>
              <p className={styles.successText}>
                Added <strong>{freePickSuccessCount}</strong> scheduled time slot(s) in one event.
              </p>
              <p className={styles.successId}>Title: <strong>{formData.title}</strong></p>
            </>
          ) : unitAllSuccessCount > 0 ? (
            <>
              <p className={styles.successText}>
                Added <strong>{unitAllSuccessCount}</strong> time slot(s) for this unit (one event).
              </p>
              <p className={styles.successId}>Title: <strong>{formData.title}</strong></p>
            </>
          ) : recurringCreatedCount > 0 || recurringSkippedCount > 0 ? (
            <>
              <p className={styles.successText}>
                Created <strong>{recurringCreatedCount}</strong> session(s).
                {recurringSkippedCount > 0 && (
                  <> <strong>{recurringSkippedCount}</strong> slot(s) skipped (lecturer or room clash).</>
                )}
              </p>
              <p className={styles.successId}>Title: <strong>{formData.title}</strong></p>
            </>
          ) : (
            <>
              <p className={styles.successText}>Session created successfully</p>
              <p className={styles.successId}>Title: <strong>{formData.title}</strong></p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => navigate("/schedule")}>
            Back to schedule
          </Button>
          <Button variant="primary" onClick={resetWizard}>
            Create another
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
